/* GameDay Kits V31.3 — Homepage, My Club and Matchday Flow; approved foundations retained. */
(function () {
  "use strict";

  if (window.GDKThemeAPI && window.GDKThemeAPI.foundationVersion === "31.0") return;

  var config = window.GDKTheme || {};
  var routes = config.routes || {};
  /* Matchday Motion — Settle & Respond. CSS remains the token source. */
  var motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  var Motion = {
    enabled: true,
    sync: function () {
      var enabled = config.motion !== false && !motionQuery.matches;
      var changed = Motion.enabled !== enabled;
      Motion.enabled = enabled;
      document.documentElement.setAttribute("data-gdk-motion", enabled ? "full" : "reduced");
      if (changed) document.dispatchEvent(new CustomEvent("gdk:motion-change", { detail: { enabled: enabled } }));
    },
    duration: function (token, fallback) {
      if (!Motion.enabled) return 0;
      var value = window.getComputedStyle(document.documentElement).getPropertyValue(token).trim();
      var duration = parseFloat(value);
      return Number.isFinite(duration) ? duration * (/ms$/.test(value) ? 1 : 1000) : fallback;
    }
  };
  if (motionQuery.addEventListener) motionQuery.addEventListener("change", Motion.sync);
  else motionQuery.addListener(Motion.sync);
  Motion.sync();

  /* A scope owns only enhancement resources, never a submitted cart mutation. */
  var componentScopes = new Set();
  var scopeRegistry = new WeakMap();
  function componentScope(element, key) {
    var registry = scopeRegistry.get(element);
    if (!registry) { registry = new Map(); scopeRegistry.set(element, registry); }
    if (registry.has(key)) return null;
    var cleanups = new Set();
    var timers = new Map();
    var frames = new Map();
    var scope = {
      element: element,
      active: true,
      add: function (cleanup) {
        var release = function () {
          if (!cleanups.delete(release)) return;
          cleanup();
        };
        cleanups.add(release);
        return release;
      },
      on: function (target, type, callback, options) {
        var off;
        var listener = function (event) {
          if (options && options.once) off();
          if (scope.active) callback.call(target, event);
        };
        target.addEventListener(type, listener, options);
        off = scope.add(function () { target.removeEventListener(type, listener, options); });
        return off;
      },
      media: function (query, callback) {
        if (query.addEventListener) return scope.on(query, "change", callback);
        query.addListener(callback);
        return scope.add(function () { query.removeListener(callback); });
      },
      timeout: function (callback, delay) {
        if (!scope.active) return 0;
        var id = window.setTimeout(function () {
          timers.delete(id);
          if (scope.active) callback();
        }, delay);
        timers.set(id, true);
        return id;
      },
      clearTimeout: function (id) { window.clearTimeout(id); timers.delete(id); },
      frame: function (callback) {
        if (!scope.active) return 0;
        var id = window.requestAnimationFrame(function (time) {
          frames.delete(id);
          if (scope.active) callback(time);
        });
        frames.set(id, true);
        return id;
      },
      cancelFrame: function (id) { window.cancelAnimationFrame(id); frames.delete(id); },
      dispose: function () {
        if (!scope.active) return;
        scope.active = false;
        timers.forEach(function (_, id) { window.clearTimeout(id); });
        frames.forEach(function (_, id) { window.cancelAnimationFrame(id); });
        timers.clear();
        frames.clear();
        Array.from(cleanups).reverse().forEach(function (cleanup) { cleanup(); });
        registry.delete(key);
        if (!registry.size) scopeRegistry.delete(element);
        componentScopes.delete(scope);
      }
    };
    registry.set(key, scope);
    componentScopes.add(scope);
    return scope;
  }

  function disposeComponents(root) {
    Array.from(componentScopes).forEach(function (scope) {
      if (scope.element === root || root.contains(scope.element)) scope.dispose();
    });
  }

  function componentElements(selector, root) {
    var elements = all(selector, root);
    if (root && root.matches && root.matches(selector)) elements.unshift(root);
    return elements;
  }
  var cartMutation = Promise.resolve();
  /* V31.1 transactions survive their presentation; only reads are cancellable. */
  var cartWorkCount = 0;
  var cartReadPromise = null;
  var currentCart = null;
  var lastRenderedCart = null;
  var cartUnverified = false;
  var pendingCartGroups = new Set();
  var pendingCartLocks = new Set();
  var purchases = new Map();
  var commerceNotice = null;

  function commerceError(code, message, cause) {
    var error = new Error(message);
    error.code = code;
    error.cause = cause;
    return error;
  }

  function validateCart(cart) {
    if (!cart || !Array.isArray(cart.items) || !Number.isFinite(cart.total_price) ||
        cart.items.some(function (item) { return !item.key || !Number.isFinite(item.quantity) || item.quantity < 1; })) {
      throw commerceError("cart-invalid", "Your bag could not be read. Refresh your bag to check its contents.");
    }
    return cart;
  }

  function purchaseKey(items) {
    return JSON.stringify(items.map(function (item) {
      var properties = {};
      Object.keys(item.properties || {}).sort().forEach(function (key) {
        if (key.charAt(0) !== "_") properties[key] = item.properties[key];
      });
      return [Number(item.id), Number(item.quantity), properties];
    }));
  }

  function itemIdentity(item) {
    var properties = {};
    Object.keys(item.properties || {}).sort().forEach(function (key) { properties[key] = item.properties[key]; });
    return JSON.stringify([Number(item.variant_id || item.id), properties]);
  }

  function groupIdentity(item) {
    var group = item.properties && item.properties._gdk_group_id || item.groupId;
    return group ? "group:" + group : "line:" + item.key;
  }

  function resolveCartGroup(cart, lock) {
    var groups = configuredGroups(cart);
    var group = groups.find(function (candidate) {
      return candidate.id === lock.identity || candidate.id === lock.resolved ||
        candidate.items.some(function (item) { return item.key === lock.key; });
    });
    if (!group && lock.signature && !lock.removing) {
      var matches = groups.filter(function (candidate) {
        return candidate.id.indexOf("line:") === 0 && candidate.items.length === 1 && itemIdentity(candidate.items[0]) === lock.signature;
      });
      if (matches.length === 1) group = matches[0];
    }
    return group;
  }

  function bindCartLock(lock, group) {
    if (!group) return;
    if (lock.resolved && lock.resolved !== lock.identity) pendingCartGroups.delete(lock.resolved);
    lock.resolved = group.id;
    pendingCartGroups.add(group.id);
  }

  function configuredGroups(cart) {
    var groups = new Map();
    (cart.items || []).forEach(function (item) {
      var id = groupIdentity(item);
      if (!groups.has(id)) groups.set(id, { id: id, items: [], products: [], services: [], total: 0 });
      var group = groups.get(id);
      group.items.push(item);
      group[itemRole(item) === "service" ? "services" : "products"].push(item);
      group.total += Number(item.final_line_price || 0);
    });
    return Array.from(groups.values());
  }

  function cartIntegrityProblem(cart) {
    return configuredGroups(cart).some(function (group) {
      if (group.services.length && !group.products.length) return true;
      if (group.id.indexOf("group:") !== 0) return false;
      if (!group.products.length || !group.services.length) return true;
      var shirts = group.products.reduce(function (n, item) { return n + item.quantity; }, 0);
      var services = group.services.reduce(function (n, item) { return n + item.quantity; }, 0);
      var parent = group.products[0].properties || {};
      return shirts !== services || group.items.some(function (item) {
        var props = item.properties || {};
        return props.Name !== parent.Name || props.Number !== parent.Number;
      });
    });
  }

  function recoveryContent(host, message) {
    if (!host) return;
    host.hidden = false;
    host.replaceChildren(create("p", "", message));
    var button = create("button", "gdk-text-link", "Refresh bag");
    button.type = "button";
    button.setAttribute("data-cart-recover", "");
    var link = create("a", "gdk-text-link", "View bag");
    link.href = routes.cart || "/cart";
    host.append(button, document.createTextNode(" · "), link);
  }

  function purchaseFeedback(host, message, recover) {
    if (!host || !host.isConnected) return;
    var feedback = one("[data-commerce-feedback], [data-product-error]", host);
    if (!feedback) {
      feedback = create("div", "gdk-form-error");
      feedback.setAttribute("data-commerce-feedback", "");
      feedback.setAttribute("role", "status");
      host.appendChild(feedback);
    }
    feedback.hidden = false;
    if (recover) recoveryContent(feedback, message);
    else feedback.textContent = message;
  }

  function applyCartState() {
    all("[data-cart-line]").forEach(function (line) {
      var id = cartLineIdentity(line);
      var busy = pendingCartGroups.has(id);
      line.toggleAttribute("aria-busy", busy);
      line.dataset.gdkCartPending = busy ? "true" : "false";
      all("button, a[data-cart-remove]", line).forEach(function (control) {
        if (busy) {
          if (!control.hasAttribute("data-cart-was-disabled")) {
            control.setAttribute("data-cart-was-disabled", control.disabled ? "true" : "false");
            control.setAttribute("data-cart-was-tabindex", control.getAttribute("tabindex") || "");
          }
          if ("disabled" in control) control.disabled = true;
          control.setAttribute("aria-disabled", "true");
          if (control.tagName === "A") control.tabIndex = -1;
        } else if (control.hasAttribute("data-cart-was-disabled")) {
          if ("disabled" in control) control.disabled = control.getAttribute("data-cart-was-disabled") === "true";
          var tabindex = control.getAttribute("data-cart-was-tabindex");
          if (tabindex) control.setAttribute("tabindex", tabindex); else control.removeAttribute("tabindex");
          control.removeAttribute("aria-disabled");
          control.removeAttribute("data-cart-was-disabled");
          control.removeAttribute("data-cart-was-tabindex");
        }
      });
    });
    var broken = currentCart && cartIntegrityProblem(currentCart);
    all("button[name='checkout']").forEach(function (button) {
      var blocked = Boolean(broken || cartWorkCount || cartUnverified);
      if (blocked && !button.hasAttribute("data-commerce-disabled")) button.setAttribute("data-commerce-disabled", button.disabled ? "true" : "false");
      if (blocked) button.disabled = true;
      else if (button.hasAttribute("data-commerce-disabled")) {
        button.disabled = button.getAttribute("data-commerce-disabled") === "true";
        button.removeAttribute("data-commerce-disabled");
      }
    });
    all("[data-cart-body], [data-cart-page-lines]").forEach(function (surface) {
      var notice = one("[data-cart-recovery]", surface);
      var message = broken ? "A personalised shirt and its printing service do not match. Remove the incomplete group before checkout, or contact support." : commerceNotice;
      if (!message && cartUnverified && !cartWorkCount && !cartRefreshController) message = "Your bag needs to be refreshed before checkout. Check its contents before trying another change.";
      if (!message) { if (notice) notice.remove(); return; }
      if (!notice) { notice = create("div", "gdk-form-error"); notice.setAttribute("data-cart-recovery", ""); surface.prepend(notice); }
      if (notice.dataset.message !== message) {
        recoveryContent(notice, message);
        notice.dataset.message = message;
      }
    });
  }

  function setCommerceNotice(message) {
    commerceNotice = message;
    applyCartState();
  }

  function acceptCart(cart) {
    validateCart(cart);
    currentCart = cart;
    pendingCartLocks.forEach(function (lock) { bindCartLock(lock, resolveCartGroup(cart, lock)); });
    var renderedState = JSON.stringify(cart);
    if (renderedState !== lastRenderedCart) {
      renderCart(cart);
      lastRenderedCart = renderedState;
    } else {
      updateCartContext(cart);
    }
    cartUnverified = false;
    purchases.forEach(function (entry, key) { if (entry.state === "added-unreconciled") purchases.delete(key); });
    commerceNotice = null;
    applyCartState();
    lastCartRefreshAt = Date.now();
    document.dispatchEvent(new CustomEvent("gdk:cart:reconciled", { detail: { cart: cart } }));
    return cart;
  }

  function outcomeMessage(result) {
    return result.reconciled ? "Added to your bag." : "Added to your bag, but the bag could not refresh. Refresh your bag before adding this shirt again.";
  }

  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-cart-recover]");
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;
    button.disabled = true;
    var version = OverlayController.version();
    var owner = button.closest("[data-product-root], [data-overlay], [data-cart-page]") || document.body;
    var returnTarget = one("[data-cart-trigger], [data-overlay-open='cart']") || one("#MainContent");
    refreshCart({ silent: true }).then(function () {
      purchases.forEach(function (entry, key) { if (entry.state !== "pending") purchases.delete(key); });
      all("[data-commerce-feedback], [data-product-error]").forEach(function (feedback) {
        if (one("[data-cart-recover]", feedback)) { feedback.hidden = true; feedback.replaceChildren(); }
      });
      if (owner.isConnected && version === OverlayController.version()) OverlayController.open("cart", returnTarget);
      announce("Bag refreshed. Review its contents before adding again.");
    }).catch(function () {
      if (button.isConnected) button.disabled = false;
      announce("Your bag is still unavailable. Use View bag to check it; do not add the same shirt again yet.");
    });
  });


  /* One normalised copy is used for preview/validation/payload; input text is never rewritten. */
  var PersonalisationRules = Object.assign({ nameMax: 12, numberMax: 2, namePattern: "^[A-ZÀ-ÖØ-Ý '-]+$" }, config.personalisationRules || {});
  var personalisationNamePattern = new RegExp(PersonalisationRules.namePattern);
  function personalisationValue(nameField, numberField, required) {
    var name = String(nameField && nameField.value || "").trim().toUpperCase();
    var number = String(numberField && numberField.value || "").trim();
    var nameError = !name ? (required ? "Enter a name for personalisation." : "") :
      name.length > PersonalisationRules.nameMax ? "Use up to " + PersonalisationRules.nameMax + " characters for the printed name." :
      !personalisationNamePattern.test(name) ? "Use the supported letters, spaces, apostrophes or hyphens shown in the name guidance." : "";
    var numberError = !number ? (required ? "Enter a number for personalisation." : "") :
      !/^[0-9]+$/.test(number) || number.length > PersonalisationRules.numberMax ? "Use 1–" + PersonalisationRules.numberMax + " digits for the number." : "";
    return { name: name, number: number, nameError: nameError, numberError: numberError, valid: !nameError && !numberError, message: nameError || numberError };
  }

  function personalisationFeedback(nameField, numberField, feedback, required) {
    var value = personalisationValue(nameField, numberField, required);
    [[nameField, value.nameError], [numberField, value.numberError]].forEach(function (entry) {
      if (!entry[0]) return;
      entry[0].setCustomValidity(entry[1]);
      if (entry[1]) entry[0].setAttribute("aria-invalid", "true");
      else entry[0].removeAttribute("aria-invalid");
    });
    if (feedback) { feedback.textContent = value.message; feedback.hidden = !value.message; }
    return value;
  }

  /* Product style is presentation data. Liquid identifies the product source;
     this single resolver validates values before homepage or PDP CSS can use it. */
  var personalisationStyleProperties = [
    "--gdk-pers-name-color",
    "--gdk-pers-number-color",
    "--gdk-pers-outline-color",
    "--gdk-pers-outline-width",
    "--gdk-pers-text-shadow"
  ];

  function normalisePersonalisationColour(value) {
    value = String(value || "").trim();
    return /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value) ? value : "";
  }

  function resolvePersonalisationStyle(source) {
    var neutral = {
      verified: false,
      status: "unverified",
      source: "neutral",
      profile: "neutral",
      nameColor: "",
      numberColor: "",
      outlineColor: "",
      shadowColor: ""
    };
    if (!source || source.getAttribute("data-gdk-pers-style") === null) return neutral;
    var status = source.getAttribute("data-gdk-pers-status") || "unverified";
    var nameColor = normalisePersonalisationColour(source.getAttribute("data-gdk-pers-name-color"));
    var numberColor = normalisePersonalisationColour(source.getAttribute("data-gdk-pers-number-color"));
    if (status !== "verified" || !nameColor || !numberColor) return Object.assign({}, neutral, {
      source: source.getAttribute("data-gdk-pers-source") || "neutral"
    });
    var profile = source.getAttribute("data-gdk-pers-profile") || "configured";
    if (!/^(?:configured|pirates-home|pirates-away)$/.test(profile)) profile = "configured";
    return {
      verified: true,
      status: "verified",
      source: source.getAttribute("data-gdk-pers-source") || "product",
      profile: profile,
      nameColor: nameColor,
      numberColor: numberColor,
      outlineColor: normalisePersonalisationColour(source.getAttribute("data-gdk-pers-outline-color")),
      shadowColor: normalisePersonalisationColour(source.getAttribute("data-gdk-pers-shadow-color"))
    };
  }

  function applyPersonalisationStyle(target, source) {
    if (!target) return resolvePersonalisationStyle(null);
    var style = resolvePersonalisationStyle(source || target);
    personalisationStyleProperties.forEach(function (property) { target.style.removeProperty(property); });
    target.setAttribute("data-gdk-pers-applied", style.status);
    target.setAttribute("data-gdk-pers-active-profile", style.profile);
    target.setAttribute("data-gdk-pers-active-source", style.source);
    if (style.verified) {
      target.style.setProperty("--gdk-pers-name-color", style.nameColor);
      target.style.setProperty("--gdk-pers-number-color", style.numberColor);
      if (style.outlineColor) {
        target.style.setProperty("--gdk-pers-outline-color", style.outlineColor);
        target.style.setProperty("--gdk-pers-outline-width", style.profile === "pirates-home" ? ".035em" : ".025em");
      }
      if (style.shadowColor) {
        target.style.setProperty("--gdk-pers-text-shadow", "0 .035em .08em " + style.shadowColor);
      }
    }
    all("[data-personalisation-style-note]", target).forEach(function (note) {
      note.textContent = note.getAttribute(style.verified ? "data-verified-copy" : "data-neutral-copy") || note.textContent;
    });
    return style;
  }

  function bindPersonalisationInputs(life, nameField, numberField, feedback, render) {
    [nameField, numberField].forEach(function (field) {
      if (!field) return;
      var oldMax = field.getAttribute("maxlength");
      field.removeAttribute("maxlength");
      life.on(field, "compositionstart", function () { field.dataset.gdkComposing = "true"; });
      function update(event) {
        if ((event && event.isComposing) || field.dataset.gdkComposing === "true") return;
        personalisationFeedback(nameField, numberField, feedback, false);
        render();
      }
      life.on(field, "compositionend", function () { delete field.dataset.gdkComposing; update(); });
      life.on(field, "input", update);
      life.on(field, "blur", update);
      life.add(function () {
        field.setCustomValidity("");
        field.removeAttribute("aria-invalid");
        delete field.dataset.gdkComposing;
        if (oldMax !== null) field.setAttribute("maxlength", oldMax);
      });
    });
  }

  var announcementTimer;
  var lastCartRefreshAt = 0;
  var cartRenderVersion = 0;
  var cartRefreshController = null;
  var cartPairLookupCache = {};
  var cartUpsellProductCache = {};
  var cartUpsellPanelSerial = 0;

  document.documentElement.classList.remove("no-js");
  document.documentElement.classList.add("js");

  document.addEventListener("error", function (event) {
    var image = event.target;
    if (image && image.matches && image.matches(".gdk-product-card__image--secondary")) image.hidden = true;
  }, true);

  window.addEventListener("hashchange", function () {
    all("[data-product-root]").forEach(function (root) {
      if (typeof root._gdkOpenPersonalisation === "function") root._gdkOpenPersonalisation(true);
    });
  });

  function one(selector, root) {
    return (root || document).querySelector(selector);
  }

  function all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function create(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function shopifyImageUrl(value, width) {
    var source = value && (value.url || value.src || value);
    if (!source || !width) return source || "";
    try {
      var url = new URL(String(source), window.location.origin);
      var isShopifyCdn = url.hostname === "cdn.shopify.com" ||
        /(?:^|\.)myshopify\.com$/i.test(url.hostname) ||
        /^\/cdn\/(?:shop|s\/files)\//.test(url.pathname);
      if (!isShopifyCdn) return String(source);
      url.searchParams.set("width", String(Math.max(1, Math.round(width))));
      return url.toString();
    } catch (error) {
      return String(source);
    }
  }

  function setCommerceImage(image, source, widths, sizes) {
    if (!image || !source) return;
    var candidates = (widths || []).map(Number).filter(function (width) {
      return Number.isFinite(width) && width > 0;
    });
    if (!candidates.length) candidates = [240];
    image.src = shopifyImageUrl(source, candidates[candidates.length - 1]);
    var responsive = candidates.map(function (width) {
      return shopifyImageUrl(source, width) + " " + width + "w";
    });
    if (new Set(responsive.map(function (candidate) { return candidate.replace(/\s+\d+w$/, ""); })).size > 1) {
      image.srcset = responsive.join(", ");
      image.sizes = sizes || candidates[0] + "px";
    }
    image.decoding = "async";
  }

  function jsonUrl(route) {
    if (!route) return "";
    return /\.js(?:\?|$)/.test(route) ? route : route.replace(/\/$/, "") + ".js";
  }

  function formatMoneyNumber(cents, decimals, thousands, decimal) {
    var numeric = Number(cents || 0) / 100;
    if (!Number.isFinite(numeric)) numeric = 0;
    var fixed = numeric.toFixed(decimals).split(".");
    fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fixed[0] + (decimals ? decimal + fixed[1] : "");
  }

  function formatMoney(cents) {
    var moneyFormat = String(config.moneyFormat || "");
    var placeholder = moneyFormat.match(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/i);
    if (placeholder) {
      var formats = {
        amount: [2, ",", "."],
        amount_no_decimals: [0, ",", "."],
        amount_with_comma_separator: [2, ".", ","],
        amount_no_decimals_with_comma_separator: [0, ".", ","],
        amount_with_apostrophe_separator: [2, "'", "."],
        amount_with_space_separator: [2, " ", ","],
        amount_no_decimals_with_space_separator: [0, " ", ","],
        amount_with_period_and_space_separator: [2, " ", "."],
        amount_no_decimals_with_period_and_space_separator: [0, " ", "."]
      };
      var rule = formats[placeholder[1].toLowerCase()] || formats.amount;
      return moneyFormat
        .replace(placeholder[0], formatMoneyNumber(cents, rule[0], rule[1], rule[2]))
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;|&#160;/gi, "\u00a0")
        .trim();
    }
    var numeric = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-ZA", {
        style: "currency",
        currency: config.currency || "ZAR"
      }).format(numeric);
    } catch (error) {
      return "R" + numeric.toFixed(2);
    }
  }

  function predictivePriceToCents(value) {
    if (value === undefined || value === null || value === "") return 0;
    if (typeof value === "number") return Math.round(value);
    var cleaned = String(value).trim().replace(/[^0-9,.-]/g, "").replace(/,(?=\d{1,2}$)/, ".");
    var numeric = Number(cleaned.replace(/,/g, ""));
    return Number.isFinite(numeric) ? Math.round(numeric * 100) : 0;
  }

  function errorMessage(error, fallback) {
    if (!error) return fallback;
    return error.description || error.message || fallback;
  }

  function announce(message) {
    var region = one("[data-gdk-status]");
    if (!region) return;
    var cart = one('[data-overlay="cart"]:not([hidden])');
    if (!region._gdkStatusHome) region._gdkStatusHome = region.parentNode;
    var destination = cart && one(".gdk-drawer__header", cart);
    (destination || region._gdkStatusHome).appendChild(region);
    region.classList.toggle("gdk-live-region--cart", Boolean(destination));
    region.textContent = message;
    region.classList.add("is-visible");
    window.clearTimeout(announcementTimer);
    announcementTimer = window.setTimeout(function () {
      region.classList.remove("is-visible");
    }, 3200);
  }

  function track(name, detail) {
    document.dispatchEvent(new CustomEvent("gdk:event", {
      detail: { name: name, data: detail || {} }
    }));
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().catch(function () {
        throw Object.assign(new Error("The server response could not be read."), { status: response.status, httpOk: response.ok });
      }).then(function (data) {
        if (!response.ok) {
          var failure = new Error(data.description || data.message || "Request failed");
          Object.assign(failure, data, { status: response.status, httpOk: false });
          throw failure;
        }
        return data;
      });
    });
  }

  /* Shared overlay controller: menu, search, filters, cart, quick add and size guide. */
  var OverlayController = (function () {
    var active = null;
    var returnFocus = null;
    var stateVersion = 0;
    var closeTimers = new Map();
    var openFrames = new Map();
    var backgroundRegions = new Set();
    var focusableSelector = [
      "a[href]:not([tabindex='-1'])",
      "button:not([disabled]):not([tabindex='-1'])",
      "summary:not([tabindex='-1'])",
      "input:not([disabled]):not([type='hidden'])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(",");

    function find(name) {
      return all("[data-overlay]").find(function (overlay) {
        return overlay.getAttribute("data-overlay") === name;
      });
    }

    function focusables(overlay) {
      return all(focusableSelector, overlay).filter(function (element) {
        return !element.hidden && element.getClientRects().length > 0;
      });
    }

    function setTriggerState(name, isOpen) {
      all("[data-overlay-open]").forEach(function (trigger) {
        if (trigger.getAttribute("data-overlay-open") === name) {
          trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
        }
      });
    }

    function setRegionState(region, isHidden) {
      if (isHidden) {
        if (region.dataset.gdkOverlayInert !== undefined) return;
        region.dataset.gdkOverlayInert = region.hasAttribute("inert") ? "true" : "false";
        region.dataset.gdkOverlayAriaHidden = region.getAttribute("aria-hidden") || "";
        backgroundRegions.add(region);
        region.setAttribute("inert", "");
        region.setAttribute("aria-hidden", "true");
      } else if (region.dataset.gdkOverlayInert !== undefined) {
        region.toggleAttribute("inert", region.dataset.gdkOverlayInert === "true");
        if (region.dataset.gdkOverlayAriaHidden) region.setAttribute("aria-hidden", region.dataset.gdkOverlayAriaHidden);
        else region.removeAttribute("aria-hidden");
        delete region.dataset.gdkOverlayInert;
        delete region.dataset.gdkOverlayAriaHidden;
        backgroundRegions.delete(region);
      }
    }

    function setContainingBranchState(region, overlay, isHidden) {
      var branch = region;
      while (branch && branch !== overlay) {
        var children = Array.prototype.slice.call(branch.children || []);
        var containingChild = children.find(function (child) {
          return child === overlay || child.contains(overlay);
        });
        children.forEach(function (child) {
          if (child !== containingChild) setRegionState(child, isHidden);
        });
        if (!containingChild) break;
        branch = containingChild;
      }
    }

    function setBackgroundState(overlay, isHidden) {
      if (!isHidden) {
        Array.from(backgroundRegions).forEach(function (region) { setRegionState(region, false); });
        return;
      }
      all("#MainContent, .gdk-footer-section, .gdk-header-section").forEach(function (region) {
        if (region.contains(overlay)) {
          setContainingBranchState(region, overlay, isHidden);
          return;
        }
        setRegionState(region, isHidden);
      });
      all(".gdk-whatsapp-link--floating").forEach(function (link) { setRegionState(link, isHidden); });
    }

    function cancelOpen(overlay) {
      window.cancelAnimationFrame(openFrames.get(overlay));
      openFrames.delete(overlay);
    }

    function open(name, trigger) {
      var overlay = find(name);
      if (!overlay) return false;
      if (active && active !== overlay) close(active, false);
      window.clearTimeout(closeTimers.get(overlay));
      closeTimers.delete(overlay);
      cancelOpen(overlay);
      active = overlay;
      stateVersion += 1;
      var openingVersion = stateVersion;
      returnFocus = trigger || document.activeElement;
      overlay.hidden = false;
      overlay.removeAttribute("inert");
      overlay.setAttribute("aria-hidden", "false");
      document.body.classList.add("gdk-overlay-open");
      setBackgroundState(overlay, true);
      setTriggerState(name, true);
      var finishOpen = function () {
        openFrames.delete(overlay);
        if (active !== overlay || stateVersion !== openingVersion || !overlay.isConnected) return;
        overlay.classList.add("is-open");
        var preferred = one("[autofocus]", overlay) || one("[data-predictive-input]", overlay);
        var candidates = focusables(overlay);
        (preferred || candidates[0] || one("[role='dialog']", overlay) || overlay).focus({ preventScroll: true });
      };
      if (Motion.enabled) openFrames.set(overlay, window.requestAnimationFrame(finishOpen));
      else finishOpen();
      document.dispatchEvent(new CustomEvent("gdk:overlay:open", { detail: { name: name, overlay: overlay } }));
      return true;
    }

    function close(target, restore, immediate) {
      var overlay = typeof target === "string" ? find(target) : (target || active);
      if (!overlay) return;
      var wasActive = active === overlay;
      var wasOpen = wasActive || overlay.classList.contains("is-open") || openFrames.has(overlay);
      var focusTarget = wasActive ? returnFocus : null;
      var name = overlay.getAttribute("data-overlay");
      if (wasOpen) stateVersion += 1;
      cancelOpen(overlay);
      overlay.classList.remove("is-open");
      overlay.setAttribute("aria-hidden", "true");
      overlay.setAttribute("inert", "");
      setTriggerState(name, false);
      if (wasActive) { active = null; returnFocus = null; }
      if (!active) {
        document.body.classList.remove("gdk-overlay-open");
        setBackgroundState(overlay, false);
      }
      window.clearTimeout(closeTimers.get(overlay));
      closeTimers.delete(overlay);
      var hide = function () {
        if (!overlay.classList.contains("is-open")) overlay.hidden = true;
        closeTimers.delete(overlay);
      };
      var delay = immediate ? 0 : Motion.duration("--gdk-motion-overlay-close", 260);
      if (delay && overlay.isConnected && wasOpen) closeTimers.set(overlay, window.setTimeout(hide, delay));
      else hide();
      if (restore !== false && focusTarget && focusTarget.isConnected && !focusTarget.closest("[inert]")) {
        focusTarget.focus({ preventScroll: true });
      }
      if (wasOpen) document.dispatchEvent(new CustomEvent("gdk:overlay:close", { detail: { name: name, overlay: overlay } }));
    }

    function dispose(root) {
      var contains = function (node) { return node && (node === root || root.contains(node)); };
      if (active && (contains(active) || contains(returnFocus))) {
        var fallback = contains(returnFocus);
        close(active, !fallback, true);
        if (fallback) {
          var main = one("#MainContent");
          if (main && !contains(main)) main.focus({ preventScroll: true });
        }
      }
      componentElements("[data-overlay]", root).forEach(function (overlay) { close(overlay, false, true); });
      Array.from(backgroundRegions).forEach(function (region) { if (contains(region)) setRegionState(region, false); });
    }

    function reconcile() {
      Array.from(backgroundRegions).forEach(function (region) { if (!region.isConnected) setRegionState(region, false); });
      if (active) setBackgroundState(active, true);
    }

    document.addEventListener("gdk:motion-change", function () {
      if (Motion.enabled) return;
      Array.from(closeTimers.keys()).forEach(function (overlay) { close(overlay, false, true); });
    });

    document.addEventListener("click", function (event) {
      var opener = event.target.closest("[data-overlay-open]");
      if (opener) {
        var name = opener.getAttribute("data-overlay-open");
        if (find(name)) {
          event.preventDefault();
          open(name, opener);
        }
        return;
      }

      var closer = event.target.closest("[data-overlay-close]");
      if (closer) {
        var overlay = closer.closest("[data-overlay]");
        if (closer.tagName === "BUTTON") event.preventDefault();
        close(overlay, closer.tagName === "BUTTON");
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        if (active) {
          event.preventDefault();
          close(active, true);
        }
        all("[data-gdk-mega-menu][open]").forEach(function (details) {
          var restoreFocus = details.contains(document.activeElement);
          details.open = false;
          var summary = one("summary", details);
          if (restoreFocus && summary) summary.focus({ preventScroll: true });
        });
        return;
      }
      if (event.key !== "Tab" || !active) return;
      var candidates = focusables(active);
      if (!candidates.length) return;
      var first = candidates[0];
      var last = candidates[candidates.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    return {
      open: open,
      close: close,
      dispose: dispose,
      reconcile: reconcile,
      version: function () { return stateVersion; },
      get active() { return active; }
    };
  })();

  function initMegaMenus(root) {
    componentElements("[data-gdk-mega-menu]", root).forEach(function (details) {
      var life = componentScope(details, "mega-menu");
      if (!life) return;
      life.on(details, "toggle", function () {
        if (!details.open) return;
        all("[data-gdk-mega-menu][open]").forEach(function (other) {
          if (other !== details) other.open = false;
        });
      });
    });
  }

  document.addEventListener("click", function (event) {
    if (!event.target.closest("[data-gdk-mega-menu]")) {
      all("[data-gdk-mega-menu][open]").forEach(function (details) { details.open = false; });
    }
  });

  /* Cart API and safe cart rendering. */
  function invalidateCartReads() {
    cartRenderVersion += 1;
    if (cartRefreshController) cartRefreshController.abort();
    cartReadPromise = null;
  }

  function getCart(signal) {
    return fetchJSON(jsonUrl(routes.cart), { headers: { Accept: "application/json" }, signal: signal }).then(validateCart);
  }

  function queueCartMutation(task) {
    cartWorkCount += 1;
    applyCartState();
    cartMutation = cartMutation.catch(function () {}).then(function () {
      invalidateCartReads();
      return task();
    }).finally(function () {
      cartWorkCount -= 1;
      applyCartState();
    });
    return cartMutation;
  }

  function addItems(items) {
    return addItemsAndRefresh(items).then(function (result) { return result.response; });
  }

  function updateLines(updates) {
    return fetchJSON(jsonUrl(routes.cartUpdate || (routes.cart || "/cart") + "/update"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates })
    });
  }

  function itemRole(item) {
    if ((item.properties && item.properties._gdk_role) === "service" || item.role === "service" || isConfiguredServiceProduct(item)) return "service";
    return "product";
  }

  function changeGroupItems(lock, action, requestedQuantity) {
    return queueCartMutation(function () {
      var ready = currentCart && !cartUnverified ? Promise.resolve(currentCart) : refreshCart({ silent: true, insideMutation: true });
      return ready.then(function (cart) {
        var group = resolveCartGroup(cart, lock);
        if (group) { bindCartLock(lock, group); applyCartState(); }
        if (!group) throw commerceError("line-missing", "This item has changed. Refresh your bag before updating it.");
        var remove = action === "remove";
        if (!remove && (group.products.length !== 1 || group.services.length > 1 ||
            (group.id.indexOf("group:") === 0 && group.services.length !== 1))) {
          throw commerceError("group-split", "This configured purchase has split or incomplete lines. Remove the group and add it again, or contact support.");
        }
        var quantity = remove ? 0 : group.products[0].quantity + (action === "increase" ? 1 : action === "decrease" ? -1 : 0);
        if (action === "quantity") quantity = requestedQuantity;
        if (!Number.isInteger(quantity) || quantity < 0) throw commerceError("quantity-invalid", "Choose a valid quantity.");
        cartUnverified = true;
        var request;
        if (quantity === 0) {
          lock.removing = true;
          var updates = {};
          group.items.forEach(function (item) { updates[item.key] = 0; });
          request = updateLines(updates);
        } else {
          var change = function (key) {
            return fetchJSON(jsonUrl((routes.cart || "/cart") + "/change"), {
              method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ id: key, quantity: quantity })
            }).then(validateCart);
          };
          request = change(group.products[0].key).then(function (changed) {
            currentCart = changed;
            if (!group.services.length) return changed;
            var updated = configuredGroups(changed).find(function (candidate) { return candidate.id === group.id; });
            if (!updated || updated.products.length !== 1 || updated.services.length !== 1 ||
                updated.products[0].quantity !== quantity) throw commerceError("quantity-unavailable", "The requested shirt quantity is unavailable.");
            return change(updated.services[0].key);
          });
        }
        return request.then(function (updated) {
          acceptCart(updated);
          var result = quantity === 0
            ? configuredGroups(updated).find(function (candidate) { return candidate.id === group.id; })
            : resolveCartGroup(updated, lock);
          if ((quantity === 0 && result) || (quantity > 0 && (!result || result.products.some(function (item) { return item.quantity !== quantity; }) ||
              result.services.some(function (item) { return item.quantity !== quantity; })))) {
            throw commerceError("quantity-unavailable", "Shopify could not apply the requested quantity.");
          }
          return { cart: updated, quantity: quantity };
        }).catch(function (error) {
          cartUnverified = true;
          return refreshCart({ silent: true, insideMutation: true }).catch(function () { return null; }).then(function () {
            throw commerceError("update-uncertain", errorMessage(error, "The bag update could not be confirmed.") + " Review your bag before trying again.", error);
          });
        });
      });
    });
  }

  function visibleCartCount(cart) {
    return (cart.items || []).reduce(function (total, item) {
      return itemRole(item) === "service" ? total : total + item.quantity;
    }, 0);
  }

  function normaliseClubKey(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function pairedKitFromCard(card) {
    if (!card) return null;
    var url = card.getAttribute("data-pair-url") || "";
    var title = card.getAttribute("data-pair-title") || "";
    if (!url || !title) return null;
    return {
      title: title,
      url: url,
      image: card.getAttribute("data-pair-image") || "",
      price: Number(card.getAttribute("data-pair-price") || 0),
      kitType: card.getAttribute("data-pair-kit-type") || ""
    };
  }

  function addPairedKitProperties(properties, pair) {
    if (!properties || !pair || !pair.url || !pair.title) return;
    properties._gdk_pair_title = String(pair.title);
    properties._gdk_pair_url = String(pair.url);
    if (pair.image) properties._gdk_pair_image = String(pair.image);
    if (Number(pair.price) > 0) properties._gdk_pair_price = String(Number(pair.price));
    if (pair.kitType) properties._gdk_pair_kit_type = String(pair.kitType);
  }

  function normaliseProductPath(value) {
    if (!value) return "";
    try {
      return new URL(String(value), window.location.origin).pathname.replace(/\/$/, "");
    } catch (error) {
      return String(value).split(/[?#]/)[0].replace(/\/$/, "");
    }
  }

  function hydrateFallbackPairContext(item, clubName, properties) {
    if (!item || !clubName || !properties) return;
    var kitType = String(properties._gdk_kit_type || "").toLowerCase();
    if (!kitType) {
      var itemTitle = String(item.product_title || item.title || "").toLowerCase();
      if (itemTitle.indexOf("away") >= 0) kitType = "away";
      else if (itemTitle.indexOf("home") >= 0) kitType = "home";
    }
    var targetType = kitType.indexOf("away") >= 0 ? "Home" : (kitType.indexOf("home") >= 0 ? "Away" : "");
    if (!targetType) return;
    var cacheKey = normaliseClubKey(clubName) + "|" + targetType.toLowerCase();
    if (!cartPairLookupCache[cacheKey]) {
      var endpoint = routes.predictiveSearch || (routes.search || "/search") + "/suggest";
      if (!/\.json(?:\?|$)/.test(endpoint)) endpoint = endpoint.replace(/\/$/, "") + ".json";
      var query = clubName + " " + targetType;
      var url = endpoint + "?q=" + encodeURIComponent(query) + "&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last";
      cartPairLookupCache[cacheKey] = fetchJSON(url, { headers: { Accept: "application/json" } }).then(function (data) {
        var found = data.resources && data.resources.results && data.resources.results.products || [];
        var currentPath = normaliseProductPath(item.url);
        return found.find(function (product) {
          var title = String(product.title || "").toLowerCase();
          var target = targetType.toLowerCase();
          return normaliseProductPath(product.url) !== currentPath &&
            title.indexOf(target) >= 0 &&
            title.indexOf("bundle") < 0 && title.indexOf("kids") < 0 && title.indexOf("junior") < 0 &&
            !isConfiguredServiceProduct(product);
        }) || null;
      }).catch(function () { return null; });
    }
    cartPairLookupCache[cacheKey].then(function (pair) {
      if (!pair) return;
      all("[data-cart-context]").forEach(function (context) {
        if (context.hidden) return;
        var label = one("[data-cart-context-name]", context);
        var titleNode = one("[data-cart-context-title]", context);
        var price = one("[data-cart-context-price]", context);
        var image = one("[data-cart-context-image]", context);
        var mediaLink = one("[data-cart-context-media-link]", context);
        var link = one("[data-cart-context-link]", context);
        var incentive = one("[data-cart-context-incentive]", context);
        if (label) label.textContent = properties._gdk_season ? "Complete the " + properties._gdk_season + " set" : "Complete the set";
        if (titleNode) titleNode.textContent = pair.title || targetType + " shirt";
        if (price) {
          price.hidden = pair.price === undefined;
          if (pair.price !== undefined) price.textContent = formatMoney(predictivePriceToCents(pair.price));
        }
        var imageUrl = pair.featured_image && (pair.featured_image.url || pair.featured_image);
        if (image) {
          image.hidden = !imageUrl;
          if (imageUrl) setCommerceImage(image, imageUrl, [120, 180, 240], "62px");
        }
        if (mediaLink) { mediaLink.hidden = !imageUrl; mediaLink.href = pair.url; }
        if (link) { link.href = pair.url; link.textContent = "View " + targetType.toLowerCase() + " shirt"; }
        if (incentive) {
          var quantity = Number(item.quantity || 0);
          var showDuoPrompt = Boolean(config.matchdayDuoEnabled && quantity === 1);
          incentive.hidden = !showDuoPrompt;
          if (showDuoPrompt) {
            var discount = Number(config.matchdayDuoDiscountPercentage || 10);
            incentive.textContent = "One more eligible shirt unlocks " + discount + "% off" + (config.matchdayDuoFreeDelivery ? " + free SA delivery" : "") + ".";
            if (label) label.textContent = "MATCHDAY DUO";
          }
        }
        setupCartUpsell(context, pair.url, item, properties, clubName, targetType);
      });
    });
  }


  function cartUpsellProduct(url) {
    var path = normaliseProductPath(url);
    if (!path || path.indexOf("/products/") < 0) return Promise.resolve(null);
    if (!cartUpsellProductCache[path]) {
      cartUpsellProductCache[path] = fetchJSON(jsonUrl(path), { headers: { Accept: "application/json" } })
        .then(function (product) { return product && product.id ? product : null; })
        .catch(function () { return null; });
    }
    return cartUpsellProductCache[path];
  }

  function clearCartUpsell(context) {
    if (!context) return;
    context._gdkUpsellToken = (context._gdkUpsellToken || 0) + 1;
    var quick = one("[data-cart-upsell-quick]", context);
    if (quick) { quick.hidden = true; quick.classList.remove("is-added"); }
  }

  function setupCartUpsell(context, destination, sourceItem, sourceProperties, clubName, targetType) {
    var quick = one("[data-cart-upsell-quick]", context);
    var detailLink = one("[data-cart-context-link]", context);
    if (!quick || !destination || !sourceItem) {
      clearCartUpsell(context);
      return;
    }
    var token = (context._gdkUpsellToken || 0) + 1;
    context._gdkUpsellToken = token;
    quick.hidden = true;
    quick.classList.remove("is-added");
    if (detailLink) { detailLink.hidden = false; detailLink.textContent = "Details"; }

    cartUpsellProduct(destination).then(function (product) {
      if (!product || context._gdkUpsellToken !== token || !document.documentElement.contains(context)) return;
      var options = normaliseOptions(product);
      var personalisationInfo = personalisationOptionInfo(options);
      var selected = options.map(function () { return null; });
      if (personalisationInfo && personalisationInfo.offValue) selected[personalisationInfo.index] = personalisationInfo.offValue;
      var visibleOptions = options.filter(function (option) {
        return !/personali[sz]ation/i.test(option.name || "") && !(option.values.length === 1 && option.values[0] === "Default Title");
      });
      var sizeOption = visibleOptions.find(function (option) { return /size/i.test(option.name || ""); });
      if (!sizeOption || visibleOptions.length !== 1) {
        clearCartUpsell(context);
        if (detailLink) { detailLink.hidden = false; detailLink.textContent = "View shirt"; }
        return;
      }

      quick.replaceChildren();
      var sizes = create("div", "gdk-cart-context__sizes");
      sizes.setAttribute("data-cart-upsell-sizes", "");
      sizes.setAttribute("aria-label", "Choose a size for " + (product.title || "paired shirt"));
      var addButton = create("button", "gdk-cart-context__add", "Choose size");
      addButton.type = "button";
      addButton.disabled = true;
      addButton.setAttribute("data-cart-upsell-add", "");
      var status = create("span", "gdk-cart-context__status");
      status.setAttribute("data-cart-upsell-status", "");
      status.setAttribute("role", "status");
      status.setAttribute("aria-live", "polite");
      quick.append(sizes, addButton, status);
      quick.hidden = false;

      var pending = false;
      function compatibleVariant(sizeValue) {
        var desired = selected.slice();
        desired[sizeOption.index] = sizeValue;
        return (product.variants || []).find(function (variant) {
          return variant.available && variant.options.every(function (value, index) {
            return desired[index] === null || desired[index] === value;
          });
        }) || null;
      }
      function selectedVariant() {
        if (!selected[sizeOption.index]) return null;
        return compatibleVariant(selected[sizeOption.index]);
      }
      function renderUpsellState() {
        all("button", sizes).forEach(function (button) {
          var value = button.getAttribute("data-size-value");
          button.setAttribute("aria-pressed", selected[sizeOption.index] === value ? "true" : "false");
          button.disabled = pending || !compatibleVariant(value);
        });
        var variant = selectedVariant();
        addButton.disabled = pending || !variant;
        if (pending) addButton.textContent = "Adding…";
        else if (!variant) addButton.textContent = "Choose size";
        else addButton.textContent = "Add " + String(targetType || "shirt").toLowerCase() + " — " + formatMoney(variant.price);
      }

      sizeOption.values.forEach(function (value) {
        var button = create("button", "", value);
        button.type = "button";
        button.setAttribute("data-size-value", value);
        button.setAttribute("aria-pressed", "false");
        button.disabled = !compatibleVariant(value);
        button.addEventListener("click", function () {
          if (pending || button.disabled) return;
          selected[sizeOption.index] = value;
          status.textContent = "";
          renderUpsellState();
        });
        sizes.appendChild(button);
      });

      addButton.addEventListener("click", function () {
        var variant = selectedVariant();
        if (!variant || pending) return;
        pending = true;
        renderUpsellState();
        status.textContent = "";
        var props = {};
        if (clubName) props._gdk_club = clubName;
        if (sourceProperties && sourceProperties._gdk_season) props._gdk_season = sourceProperties._gdk_season;
        if (targetType) props._gdk_kit_type = targetType;
        props._gdk_pair_title = sourceItem.product_title || sourceItem.title || "";
        props._gdk_pair_url = sourceItem.url || "";
        var sourceImage = sourceItem.image || (sourceItem.featured_image && sourceItem.featured_image.url) || "";
        if (sourceImage) props._gdk_pair_image = sourceImage;
        var sourcePrice = Number(sourceItem.final_price || sourceItem.price || 0);
        if (sourcePrice > 0) props._gdk_pair_price = String(sourcePrice);
        if (sourceProperties && sourceProperties._gdk_kit_type) props._gdk_pair_kit_type = sourceProperties._gdk_kit_type;

        addItemsAndRefresh([{ id: Number(variant.id), quantity: 1, properties: props }])
          .then(function (result) {
            track("add_to_cart", { source: "cart_upsell", product_id: product.id, variant_id: variant.id });
            if (!quick.isConnected) {
              if (OverlayController.active && OverlayController.active.dataset.overlay === "cart") announce(outcomeMessage(result));
              return;
            }
            if (!result.reconciled) recoveryContent(status, outcomeMessage(result));
            else status.textContent = outcomeMessage(result);
          })
          .catch(function (error) {
            if (!quick.isConnected) return;
            if (error.recover) recoveryContent(status, errorMessage(error, "Review your bag before trying again."));
            else status.textContent = errorMessage(error, "The add could not complete. Your size is saved.");
          })
          .finally(function () {
            pending = false;
            if (quick.isConnected) renderUpsellState();
          });
      });
      renderUpsellState();
    });
  }

  function prunePdpRecommendationDupes(root, cart) {
    var productRoot = one("[data-product-root]");
    var recommendations = one("product-recommendations", root || document) || one("product-recommendations");
    if (!productRoot || !recommendations) return;
    var excluded = new Set();
    var currentPath = normaliseProductPath(window.location.pathname);
    if (currentPath) excluded.add(currentPath);
    var pairPath = normaliseProductPath(productRoot.getAttribute("data-paired-kit-url"));
    if (pairPath) excluded.add(pairPath);
    (cart && cart.items || []).forEach(function (item) {
      var path = normaliseProductPath(item.url);
      if (path) excluded.add(path);
    });
    var cards = all("[data-product-card]", recommendations);
    cards.forEach(function (card) {
      card.hidden = excluded.has(normaliseProductPath(card.getAttribute("data-product-url")));
    });
    if (cards.length) recommendations.hidden = !cards.some(function (card) { return !card.hidden; });
  }

  function inferCartKitType(item) {
    if (!item) return "";
    var properties = item.properties || {};
    var explicitType = String(properties._gdk_kit_type || "").toLowerCase();
    if (explicitType) return explicitType;
    var title = String(item.product_title || item.title || "").toLowerCase();
    if (title.indexOf("away") >= 0) return "away";
    if (title.indexOf("home") >= 0) return "home";
    return "";
  }

  function fetchPredictivePairProduct(item, clubName) {
    var kitType = inferCartKitType(item);
    var targetType = kitType.indexOf("away") >= 0 ? "Home" : (kitType.indexOf("home") >= 0 ? "Away" : "");
    if (!clubName || !targetType) return Promise.resolve(null);
    var cacheKey = normaliseClubKey(clubName) + "|pair|" + targetType.toLowerCase();
    if (!cartPairLookupCache[cacheKey]) {
      var endpoint = routes.predictiveSearch || (routes.search || "/search") + "/suggest";
      if (!/\.json(?:\?|$)/.test(endpoint)) endpoint = endpoint.replace(/\/$/, "") + ".json";
      var query = clubName + " " + targetType;
      var currentPath = normaliseProductPath(item && item.url);
      var url = endpoint + "?q=" + encodeURIComponent(query) + "&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last";
      cartPairLookupCache[cacheKey] = fetchJSON(url, { headers: { Accept: "application/json" } }).then(function (data) {
        var found = data.resources && data.resources.results && data.resources.results.products || [];
        var pair = found.find(function (product) {
          var title = String(product.title || "").toLowerCase();
          return normaliseProductPath(product.url) !== currentPath &&
            title.indexOf(targetType.toLowerCase()) >= 0 &&
            title.indexOf("bundle") < 0 && title.indexOf("kids") < 0 && title.indexOf("junior") < 0 &&
            !isConfiguredServiceProduct(product);
        }) || null;
        if (!pair || !pair.url) return null;
        return cartUpsellProduct(pair.url);
      }).catch(function () { return null; });
    }
    return cartPairLookupCache[cacheKey];
  }

  function buildCartUpsellCandidates(sourceItem, facts) {
    var properties = sourceItem.properties || {};
    function candidate(url, key) {
      if (!url) return Promise.resolve(null);
      return cartUpsellProduct(url).then(function (product) {
        if (!product || isConfiguredServiceProduct(product) || !(product.variants || []).some(function (v) { return v.available; })) return null;
        if (key === "pair" && Number(product.id) !== Number(facts.pairId)) return null;
        return {
          key: key, eyebrow: key === "same" ? "ADD ANOTHER" : (inferCartKitType({ title: product.title }) || "PAIRED SHIRT"),
          source: key === "same" ? "same_shirt" : "matching_shirt",
          sourceItem: sourceItem, product: product, clubName: properties._gdk_club || "",
          kitType: inferCartKitType({ title: product.title })
        };
      });
    }
    return Promise.all([candidate(facts.pairUrl, "pair"), candidate(sourceItem.url, "same")]).then(function (items) { return items.filter(Boolean); });
  }

  function findSingleVisibleOption(product) {
    var options = normaliseOptions(product).filter(function (option) {
      return !(option.values.length === 1 && option.values[0] === "Default Title") && !/personali[sz]ation/i.test(option.name || "");
    });
    return options.length === 1 ? options[0] : null;
  }

  function addCartUpsellItem(candidate, variant) {
    var sourceItem = candidate.sourceItem || {};
    var sourceProperties = sourceItem.properties || {};
    var props = {
      _gdk_club: candidate.clubName || sourceProperties._gdk_club || "",
      _gdk_season: sourceProperties._gdk_season || "",
      _gdk_kit_type: candidate.kitType || "",
      _gdk_upsell_source: candidate.source
    };
    if (candidate.key === "pair") {
      props._gdk_pair_title = sourceItem.product_title || sourceItem.title || "";
      props._gdk_pair_url = sourceItem.url || "";
      props._gdk_pair_kit_type = inferCartKitType(sourceItem) || sourceProperties._gdk_kit_type || "";
      var sourceImage = sourceItem.image || (sourceItem.featured_image && sourceItem.featured_image.url) || "";
      if (sourceImage) props._gdk_pair_image = sourceImage;
      var sourcePrice = Number(sourceItem.final_price || sourceItem.price || 0);
      if (sourcePrice > 0) props._gdk_pair_price = String(sourcePrice);
    } else if (sourceProperties._gdk_pair_url) {
      props._gdk_pair_title = sourceProperties._gdk_pair_title || "";
      props._gdk_pair_url = sourceProperties._gdk_pair_url || "";
      props._gdk_pair_kit_type = sourceProperties._gdk_pair_kit_type || "";
      if (sourceProperties._gdk_pair_image) props._gdk_pair_image = sourceProperties._gdk_pair_image;
      if (sourceProperties._gdk_pair_price) props._gdk_pair_price = sourceProperties._gdk_pair_price;
    }
    Object.keys(props).forEach(function (key) {
      if (!props[key]) delete props[key];
    });
    return addItemsAndRefresh([{ id: Number(variant.id), quantity: 1, properties: props }]);
  }

  function createCartUpsellCard(candidate) {
    var product = candidate.product;
    var options = normaliseOptions(product);
    var printing = personalisationOptionInfo(options);
    if (printing) printing.offValue = options[printing.index].values.find(function (value) {
      return /^(no\s+personali[sz]ation|none|no|without(?:\s+personali[sz]ation)?|plain)$/i.test(String(value).trim());
    }) || null;
    var size = findSingleVisibleOption(product);
    // Quick add is deliberately limited to an explicit size and a verified plain variant.
    if (!size || !/size/i.test(size.name) || (printing && !printing.offValue)) return null;
    function variantFor(value) {
      return (product.variants || []).find(function (variant) {
        return variant.available && variant.options.every(function (entry, index) {
          if (index === size.index) return String(entry) === String(value);
          if (printing && index === printing.index) return entry === printing.offValue;
          return entry === "Default Title";
        });
      });
    }
    if (!size.values.some(variantFor)) return null;
    var card = create("article", "gdk-cart-upsell-card");
    card.setAttribute("data-upsell-key", candidate.key);
    var top = create("div", "gdk-cart-upsell-card__top");
    var media = create("a", "gdk-cart-upsell-card__media");
    media.href = product.url || candidate.sourceItem.url;
    media.setAttribute("aria-label", product.title);
    if (product.featured_image) {
      var image = create("img");
      image.alt = product.title; image.loading = "lazy"; image.width = 56; image.height = 70;
      setCommerceImage(image, product.featured_image, [120, 180], "56px");
      media.appendChild(image);
    }
    var copy = create("div", "gdk-cart-upsell-card__copy");
    copy.appendChild(create("span", "gdk-cart-upsell-card__eyebrow", candidate.eyebrow));
    copy.appendChild(create("strong", "gdk-cart-upsell-card__title", product.title));
    var price = create("small", "gdk-cart-upsell-card__price", formatMoney(product.price));
    copy.appendChild(price); top.append(media, copy); card.appendChild(top);
    var quick = create("button", "gdk-cart-upsell-card__quick", "QUICK ADD");
    quick.type = "button";
    quick.setAttribute("aria-expanded", "false");
    quick.setAttribute("aria-label", "Quick add " + product.title);
    var controls = create("div", "gdk-cart-upsell-card__controls");
    controls.id = "GdkCartSizes-" + (++cartUpsellPanelSerial);
    controls.hidden = true;
    quick.setAttribute("aria-controls", controls.id);
    var sizes = create("div", "gdk-cart-upsell-card__sizes");
    sizes.setAttribute("role", "group");
    sizes.setAttribute("aria-label", "Choose size for " + product.title);
    var selected = "";
    var pending = false;
    var add = create("button", "gdk-cart-upsell-card__add", "ADD TO BAG");
    add.type = "button"; add.disabled = true;
    add.setAttribute("aria-label", "Add " + product.title + " to bag");
    var status = create("span", "gdk-cart-upsell-card__status");
    status.setAttribute("role", "status");
    function renderState() {
      var variant = variantFor(selected);
      all("[data-size-value]", sizes).forEach(function (button) {
        button.disabled = pending || !variantFor(button.dataset.sizeValue);
        button.setAttribute("aria-pressed", String(button.dataset.sizeValue === selected));
      });
      quick.disabled = pending;
      add.disabled = pending || !variant;
      add.textContent = pending ? "ADDING…" : "ADD TO BAG";
      price.textContent = formatMoney(variant ? variant.price : product.price);
    }
    size.values.forEach(function (value) {
      var button = create("button", "", value);
      button.type = "button";
      button.dataset.sizeValue = value;
      button.setAttribute("aria-label", value + (variantFor(value) ? "" : " — sold out"));
      button.setAttribute("aria-pressed", "false");
      button.disabled = !variantFor(value);
      button.addEventListener("click", function () {
        if (pending || button.disabled) return;
        selected = value; status.textContent = ""; renderState();
      });
      sizes.appendChild(button);
    });
    card._gdkCloseSizes = function () {
      if (pending) return;
      controls.hidden = true;
      card.classList.remove("is-selecting-size");
      quick.setAttribute("aria-expanded", "false");
      quick.setAttribute("aria-label", "Quick add " + product.title);
      quick.textContent = "QUICK ADD";
    };
    quick.addEventListener("click", function () {
      if (!controls.hidden) { card._gdkCloseSizes(); return; }
      var zone = card.closest("[data-cart-upsell-zone]");
      all("[data-upsell-key]", zone).forEach(function (other) {
        if (other !== card && other._gdkCloseSizes) other._gdkCloseSizes();
      });
      controls.hidden = false;
      quick.setAttribute("aria-expanded", "true");
      card.classList.add("is-selecting-size");
      quick.setAttribute("aria-label", "Collapse size choices for " + product.title);
      quick.textContent = "CHOOSE SIZE";
      var rail = card.closest("[data-cart-upsell-grid]");
      if (rail) {
        var cardBox = card.getBoundingClientRect(), railBox = rail.getBoundingClientRect();
        if (cardBox.right > railBox.right) rail.scrollLeft += cardBox.right - railBox.right;
        else if (cardBox.left < railBox.left) rail.scrollLeft -= railBox.left - cardBox.left;
      }
      var first = one("[data-size-value]:not(:disabled)", sizes);
      if (first) first.focus({ preventScroll: true });
      var commerce = card.closest(".gdk-drawer__commerce");
      if (commerce) {
        var overflow = controls.getBoundingClientRect().bottom - commerce.getBoundingClientRect().bottom;
        if (overflow > 0) commerce.scrollTop += overflow + 8;
      }
    });
    add.addEventListener("click", function () {
      var variant = variantFor(selected);
      if (!variant || pending || cartWorkCount || cartUnverified) return;
      pending = true; status.textContent = ""; renderState();
      var added = false;
      addCartUpsellItem(candidate, variant).then(function (result) {
        added = result.reconciled;
        track("add_to_cart", { source: "cart_upsell_" + candidate.key, product_id: product.id, variant_id: variant.id });
        if (!card.isConnected) { announce(outcomeMessage(result)); return; }
        if (!result.reconciled) recoveryContent(status, outcomeMessage(result));
        else status.textContent = outcomeMessage(result);
      }).catch(function (error) {
        if (!card.isConnected) return;
        if (error.recover) recoveryContent(status, errorMessage(error, "Review your bag before trying again."));
        else status.textContent = errorMessage(error, "The add could not complete. Your size is saved.");
      }).finally(function () { pending = false; if (card.isConnected) { if (added) card._gdkCloseSizes(); renderState(); } });
    });
    controls.append(sizes, add, status); card.append(quick, controls);
    renderState();
    return card;
  }

  var cartOfferFacts = {};
  var cartOfferRevision = 0;
  function cartOfferSignature(items) {
    return items.map(function (item) { return item.key + ":" + item.quantity; }).sort().join("|");
  }
  function readCartOfferFacts(root, cart) {
    var node = one("[data-cart-offer-data]", root);
    if (!node) return false;
    try {
      var data = JSON.parse(node.textContent);
      if (!Array.isArray(data.items) || cartOfferSignature(data.items) !== cartOfferSignature(cart.items || [])) return false;
      data.items.forEach(function (item) {
        cartOfferFacts[item.productId] = item;
        if (item.pairId && !cartOfferFacts[item.pairId]) cartOfferFacts[item.pairId] = { qualifies: true };
      });
      return true;
    } catch (_) { return false; }
  }
  function qualifyingCartShirts(cart) {
    var unknown = false;
    var shirts = (cart.items || []).filter(function (item) {
      if (itemRole(item) === "service") return false;
      var facts = cartOfferFacts[item.product_id];
      if (!facts) { unknown = true; return false; }
      return facts.qualifies === true;
    });
    return unknown ? null : shirts;
  }
  function updateCartDuo(cart) {
    var shirts = qualifyingCartShirts(cart);
    var count = shirts && shirts.reduce(function (sum, item) { return sum + Number(item.quantity); }, 0);
    all("[data-cart-duo]").forEach(function (panel) {
      panel.hidden = !count;
      if (!count) return;
      panel.classList.toggle("is-unlocked", count >= 2);
      var eyebrow = one("[data-cart-duo-eyebrow]", panel);
      var title = one("[data-cart-duo-title]", panel);
      var copy = one("[data-cart-duo-copy]", panel);
      var progress = one("[data-cart-offer-progress]", panel);
      if (eyebrow) eyebrow.textContent = count >= 2 ? "MATCHDAY OFFER UNLOCKED ✓" : "MATCHDAY OFFER";
      if (title) title.textContent = count >= 2 ? "You qualified for 10% OFF + FREE standard delivery." : "Add 1 more shirt to unlock 10% OFF + FREE standard delivery";
      if (copy) { copy.hidden = count >= 2; copy.textContent = "Any second qualifying shirt counts."; }
      if (progress) progress.hidden = count >= 2;
    });
    return count === 1 ? shirts[0] : null;
  }
  function paintCartOffer(cart) {
    var source = updateCartDuo(cart);
    all("[data-cart-upsell-zone]").forEach(function (zone) {
      var facts = source && cartOfferFacts[source.product_id];
      var signature = source ? source.key + "|" + (facts.pairUrl || "") : "";
      // Preserve size/error state when a rejected add returns an unchanged cart.
      if (signature && zone._gdkOfferSignature === signature) return;
      zone._gdkOfferSignature = signature;
      zone._gdkToken = (zone._gdkToken || 0) + 1;
      var token = zone._gdkToken;
      var grid = one("[data-cart-upsell-grid]", zone);
      zone.hidden = !source;
      if (!grid) return;
      grid.replaceChildren();
      if (!source) return;
      grid.appendChild(create("p", "gdk-cart-upsell-zone__loading", "Loading shirt options…"));
      buildCartUpsellCandidates(source, facts).then(function (candidates) {
        if (zone._gdkToken !== token || !zone.isConnected) return;
        grid.replaceChildren();
        candidates.forEach(function (candidate) {
          var card = createCartUpsellCard(candidate);
          if (card) grid.appendChild(card);
        });
        zone.hidden = !grid.children.length;
        if (zone.hidden) zone._gdkOfferSignature = "";
      }).catch(function () {
        if (zone._gdkToken !== token || !zone.isConnected) return;
        grid.replaceChildren(); zone.hidden = true; zone._gdkOfferSignature = "";
      });
    });
  }
  function updateCartContext(cart) {
    all("[data-cart-context]").forEach(function (context) { context.hidden = true; clearCartUpsell(context); });
    if (!one("[data-cart-duo], [data-cart-upsell-zone]")) return;
    var revision = ++cartOfferRevision;
    cartUpsellProductCache = {};
    var initial = readCartOfferFacts(document, cart);
    paintCartOffer(cart);
    if (initial) return;
    var url = new URL(routes.cart || "/cart", window.location.origin);
    url.searchParams.set("section_id", "gdk-cart-offer-data");
    fetch(url.pathname + url.search, { headers: { Accept: "text/html" }, cache: "no-store" }).then(function (response) {
      if (!response.ok) throw new Error("Offer data unavailable");
      return response.text();
    }).then(function (html) {
      if (revision !== cartOfferRevision) return;
      var doc = new DOMParser().parseFromString(html, "text/html");
      if (readCartOfferFacts(doc, cart)) paintCartOffer(cart);
    }).catch(function () { /* Offer data is optional; checkout remains available. */ });
  }

  function updateCartChrome(cart) {
    var count = visibleCartCount(cart);
    all("[data-cart-count]").forEach(function (node) {
      node.textContent = count;
      node.hidden = count === 0;
    });
    all("[data-cart-count-label]").forEach(function (node) {
      node.textContent = count ? "(" + count + ")" : "";
    });
    all("[data-cart-trigger]").forEach(function (node) {
      node.setAttribute("aria-label", "Open bag, " + count + " " + (count === 1 ? "item" : "items"));
    });
    all("[data-cart-page-count]").forEach(function (node) {
      node.textContent = count + " " + (count === 1 ? "shirt" : "shirts");
    });
    all("[data-cart-subtotal]").forEach(function (node) {
      node.textContent = formatMoney(cart.total_price);
    });
    all("[data-cart-footer]").forEach(function (node) {
      node.hidden = count === 0;
    });
    updateCartContext(cart);
    updateShippingProgress(cart.total_price);
    prunePdpRecommendationDupes(document, cart);
  }

  function updateShippingProgress(total) {
    all("[data-shipping-progress]").forEach(function (progress) {
      var threshold = Number(progress.getAttribute("data-threshold") || config.freeShippingThreshold || 0);
      if (!threshold) {
        progress.hidden = true;
        return;
      }
      var ratio = Math.max(0, Math.min(total / threshold, 1));
      var message = one("[data-shipping-message]", progress);
      var bar = one("[data-shipping-bar]", progress);
      if (message) {
        message.textContent = total >= threshold
          ? "Your order qualifies for free shipping."
          : "Add " + formatMoney(threshold - total) + " for free shipping.";
      }
      if (bar) bar.style.width = Math.round(ratio * 100) + "%";
    });
  }

  function cleanCartVariantTitle(item) {
    var optionValues = Array.isArray(item && item.options_with_values) ? item.options_with_values : [];
    if (optionValues.length) {
      return optionValues.filter(function (option) {
        return !/personalis/i.test(String(option.name || ""));
      }).map(function (option) {
        return String(option.value || "").trim();
      }).filter(function (value) {
        return value && !/^default/i.test(value);
      }).join(" / ");
    }
    return String(item && item.variant_title || "").split(" / ").filter(function (value) {
      return value && !/personalis|add name|no personal/i.test(value);
    }).join(" / ");
  }

  function appendVisibleProperties(parent, properties, hidePersonalisation) {
    Object.keys(properties || {}).forEach(function (key) {
      if (!key || key.charAt(0) === "_" || properties[key] === "") return;
      if (hidePersonalisation && (key === "Name" || key === "Number")) return;
      parent.appendChild(create("span", "gdk-cart-line__property", key + ": " + properties[key]));
    });
  }

  function createQuantity(item, groupItems) {
    var quantity = create("div", "gdk-quantity");
    quantity.setAttribute("data-quantity", "");
    var minus = create("button", "", "−");
    minus.type = "button";
    minus.setAttribute("aria-label", "Decrease " + item.product_title + " quantity");
    minus.setAttribute("data-cart-quantity", String(Math.max(0, item.quantity - 1)));
    minus.setAttribute("data-cart-action", "decrease");
    var value = create("span", "", item.quantity);
    value.setAttribute("aria-live", "polite");
    var plus = create("button", "", "+");
    plus.type = "button";
    plus.setAttribute("aria-label", "Increase " + item.product_title + " quantity");
    plus.setAttribute("data-cart-quantity", String(item.quantity + 1));
    plus.setAttribute("data-cart-action", "increase");
    var split = groupItems.filter(function (line) { return itemRole(line) !== "service"; }).length !== 1 ||
      groupItems.filter(function (line) { return itemRole(line) === "service"; }).length > 1;
    minus.disabled = split;
    plus.disabled = split;
    if (split) quantity.title = "Remove this configured group to change its quantity, or contact support.";
    quantity.append(minus, value, plus);
    quantity._groupItems = groupItems;
    return quantity;
  }

  function createCartLine(item, groupItems, isService) {
    var groupId = item.properties && item.properties._gdk_group_id;
    var hasLinkedParent = isService && groupId && groupItems.some(function (groupItem) {
      return itemRole(groupItem) !== "service";
    });
    var linkedServices = !isService ? groupItems.filter(function (groupItem) {
      return itemRole(groupItem) === "service";
    }) : [];
    var serviceTotal = linkedServices.reduce(function (total, service) {
      return total + Number(service.final_line_price || 0);
    }, 0);
    var lineClass = "gdk-cart-line" + (isService ? " gdk-cart-line--service" : "");
    if (hasLinkedParent) lineClass += " gdk-cart-line--service-linked";
    var line = create("article", lineClass);
    line.setAttribute("data-cart-line", "");
    line.setAttribute("data-line-key", item.key);
    line.setAttribute("data-line-role", isService ? "service" : "product");
    line.setAttribute("data-variant-id", String(item.id || ""));
    if (groupId) line.setAttribute("data-group-id", groupId);

    var media = create("a", "gdk-cart-line__media");
    media.href = item.url || routes.cart || "#";
    media.tabIndex = -1;
    media.setAttribute("aria-hidden", "true");
    if (item.image) {
      var image = create("img");
      setCommerceImage(image, item.image, [120, 180, 270], "90px");
      image.alt = "";
      image.width = 90;
      image.height = 112;
      image.loading = "lazy";
      media.appendChild(image);
    } else {
      media.appendChild(create("span", "gdk-cart-line__media-placeholder"));
    }

    var content = create("div", "gdk-cart-line__content");
    var eyebrow = isService
      ? "Personalisation service"
      : ((item.properties && item.properties._gdk_club) || item.vendor || "Matchday shirt");
    content.appendChild(create("span", "gdk-cart-line__eyebrow", eyebrow));
    var title = create("a", "gdk-cart-line__title", item.product_title);
    title.href = item.url || routes.cart || "#";
    content.appendChild(title);
    var visibleVariantTitle = cleanCartVariantTitle(item);
    if (visibleVariantTitle) {
      content.appendChild(create("span", "gdk-cart-line__variant", visibleVariantTitle));
    }
    appendVisibleProperties(content, item.properties, true);

    var personalName = item.properties && item.properties.Name || "";
    var personalNumber = item.properties && item.properties.Number || "";
    if (!isService && (personalName || personalNumber)) {
      var personalisationSummary = create("div", "gdk-cart-line__personalisation-summary");
      personalisationSummary.appendChild(create("span", "", "Personalisation"));
      var personalValue = [personalName, personalNumber].filter(Boolean).join(" · ");
      personalisationSummary.appendChild(create("strong", "", personalValue || "Custom print"));
      if (serviceTotal > 0) personalisationSummary.appendChild(create("small", "", "+" + formatMoney(serviceTotal)));
      content.appendChild(personalisationSummary);
    }

    var bottom = create("div", "gdk-cart-line__bottom");
    if (!isService) bottom.appendChild(createQuantity(item, groupItems));
    var displayedPrice = !isService && linkedServices.length ? Number(item.final_line_price || 0) + serviceTotal : item.final_line_price;
    bottom.appendChild(create("span", "gdk-cart-line__price", formatMoney(displayedPrice)));
    content.appendChild(bottom);

    if (!isService || !hasLinkedParent) {
      var remove = create("button", "gdk-cart-line__remove", "Remove");
      remove.type = "button";
      remove.setAttribute("data-cart-remove", "");
      remove.setAttribute("aria-label", "Remove " + item.product_title + (linkedServices.length ? " and its personalisation" : "") + " from bag");
      content.appendChild(remove);
    } else {
      content.appendChild(create("span", "gdk-cart-line__remove", "Linked to shirt"));
    }

    line._groupItems = groupItems;
    line.append(media, content);
    return line;
  }

  function renderCartContainer(container, cart) {
    container.replaceChildren();
    var productItems = (cart.items || []).filter(function (item) {
      return itemRole(item) !== "service";
    });
    var serviceItems = (cart.items || []).filter(function (item) {
      return itemRole(item) === "service";
    });

    if (!productItems.length && !serviceItems.length) {
      var empty = create("div", "gdk-empty-state");
      empty.setAttribute("data-cart-empty", "");
      empty.appendChild(create("h3", "", "Your bag is empty."));
      empty.appendChild(create("p", "", "Choose your club and get matchday ready."));
      var link = create("a", "gdk-button gdk-button--dark", "Shop all shirts");
      link.href = routes.allProducts || "/collections/all";
      empty.appendChild(link);
      container.appendChild(empty);
      return;
    }

    var lines = create("div", "gdk-cart-lines");
    configuredGroups(cart).forEach(function (group) {
      group.products.forEach(function (item, index) {
        lines.appendChild(createCartLine(item, index === 0 ? group.items : group.products, false));
      });
      group.services.forEach(function (service) { lines.appendChild(createCartLine(service, group.items, true)); });
    });
    container.appendChild(lines);
  }

  function renderCart(cart) {
    var scrollPositions = all(".gdk-drawer--cart .gdk-drawer__commerce").map(function (node) { return { node: node, top: node.scrollTop }; });
    updateCartChrome(cart);
    all("[data-cart-body]").forEach(function (container) { renderCartContainer(container, cart); });
    all("[data-cart-page-lines]").forEach(function (container) { renderCartContainer(container, cart); });
    applyCartState();
    scrollPositions.forEach(function (position) {
      position.node.scrollTop = Math.min(position.top, Math.max(0, position.node.scrollHeight - position.node.clientHeight));
    });
  }

  function refreshCart(options) {
    options = options || {};
    if (cartWorkCount && !options.insideMutation) {
      return cartMutation.catch(function () {}).then(function () { return refreshCart(options); });
    }
    if (cartReadPromise) return cartReadPromise;
    cartRefreshController = new AbortController();
    cartUnverified = true;
    applyCartState();
    var controller = cartRefreshController;
    var requestedVersion = cartRenderVersion;
    var read = getCart(controller.signal).then(function (cart) {
      if (controller.signal.aborted || requestedVersion !== cartRenderVersion) {
        var stale = new Error("Stale cart response");
        stale.name = "AbortError";
        throw stale;
      }
      return acceptCart(cart);
    }).catch(function (error) {
      if (error.name !== "AbortError" && !options.silent) {
        setCommerceNotice("Your bag could not refresh. Refresh it or open View bag before trying the action again.");
        announce("Your bag could not refresh. Use Refresh bag or View bag.");
      }
      throw error;
    }).finally(function () {
      if (cartRefreshController === controller) cartRefreshController = null;
      if (cartReadPromise === read) cartReadPromise = null;
      applyCartState();
    });
    cartReadPromise = read;
    return read;
  }

  function addItemsAndRefresh(items) {
    var key = purchaseKey(items);
    var existing = purchases.get(key);
    if (existing) {
      var message = existing.state === "pending"
        ? "This configuration is already being added. Please wait."
        : existing.state === "added-unreconciled"
          ? "This configuration was added. Refresh your bag before adding it again."
          : "The previous add could not be confirmed. Review your bag before adding this configuration again.";
      var duplicate = commerceError(existing.state, message);
      duplicate.recover = existing.state !== "pending";
      return Promise.reject(duplicate);
    }
    var transaction = { state: "pending" };
    purchases.set(key, transaction);
    return queueCartMutation(function () {
      function confirmed(response) {
        return refreshCart({ silent: true, insideMutation: true }).then(function (cart) {
          purchases.delete(key);
          return { response: response, cart: cart, reconciled: true };
        }).catch(function () {
          cartUnverified = true;
          transaction.state = "added-unreconciled";
          var result = { response: response, cart: null, reconciled: false };
          setCommerceNotice(outcomeMessage(result));
          return result;
        });
      }
      cartUnverified = true;
      return fetchJSON(jsonUrl(routes.cartAdd), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: items })
      }).then(confirmed, function (error) {
        // HTTP success remains success even if its response body cannot be decoded.
        if (error.httpOk) return confirmed(null);
        var rejected = error.status >= 400 && error.status < 500;
        transaction.state = "uncertain";
        return refreshCart({ silent: true, insideMutation: true }).catch(function () { return null; }).then(function (cart) {
          var present = cart && cart.items.some(function (line) {
            return items.some(function (item) {
              if (Number(line.variant_id || line.id) !== Number(item.id)) return false;
              return Object.keys(item.properties || {}).filter(function (name) { return name.charAt(0) !== "_"; }).every(function (name) {
                return String((line.properties || {})[name] || "") === String(item.properties[name]);
              });
            });
          });
          var recover = !rejected || !cart || present;
          if (!recover) purchases.delete(key);
          var message = rejected
            ? errorMessage(error, "Shopify could not complete this add.") + (recover ? " Review your bag before adding again; some items may have been added." : " Your selection is saved. Review it and try again.")
            : "We could not confirm whether this shirt was added. Review your bag before trying again.";
          setCommerceNotice(message);
          var failure = commerceError(rejected ? "add-rejected" : "add-uncertain", message, error);
          failure.recover = recover;
          throw failure;
        });
      });
    });
  }

  document.addEventListener("gdk:overlay:open", function (event) {
    if (event.detail.name === "cart" && !commerceNotice && !cartUnverified && Date.now() - lastCartRefreshAt > 750) {
      refreshCart().catch(function () {});
    }
  });

  function cartLineIdentity(line) {
    var group = line.getAttribute("data-group-id");
    if (group) return "group:" + group;
    var key = line.getAttribute("data-line-key");
    var item = currentCart && currentCart.items.find(function (candidate) { return candidate.key === key; });
    return item ? groupIdentity(item) : "key:" + key;
  }

  document.addEventListener("click", function (event) {
    var quantityButton = event.target.closest("[data-cart-quantity]");
    var removeButton = event.target.closest("[data-cart-remove]");
    if (!quantityButton && !removeButton) return;
    event.preventDefault();
    var line = event.target.closest("[data-cart-line]");
    if (!line || line.dataset.gdkCartPending === "true") return;
    var identity = cartLineIdentity(line);
    if (pendingCartGroups.has(identity)) return;
    var lineKey = line.getAttribute("data-line-key");
    var currentItem = currentCart && currentCart.items.find(function (item) { return item.key === lineKey; });
    var lock = { identity: identity, key: lineKey, resolved: null, signature: currentItem && !(currentItem.properties && currentItem.properties._gdk_group_id) ? itemIdentity(currentItem) : null };
    pendingCartLocks.add(lock);
    pendingCartGroups.add(identity);
    applyCartState();
    var action = removeButton ? "remove" : quantityButton.getAttribute("data-cart-action") ||
      (/increase/i.test(quantityButton.getAttribute("aria-label") || "") ? "increase" : "decrease");
    var version = OverlayController.version();
    var sourceSurface = line.closest("[data-cart-body], [data-cart-page-lines]");
    var focusOwned = line.contains(document.activeElement);
    changeGroupItems(lock, action, Number(quantityButton && quantityButton.getAttribute("data-cart-quantity"))).then(function (result) {
      pendingCartGroups.delete(identity);
      if (lock.resolved) pendingCartGroups.delete(lock.resolved);
      pendingCartLocks.delete(lock);
      applyCartState();
      if (!sourceSurface || !sourceSurface.isConnected || version !== OverlayController.version()) return;
      var renderedLine = all("[data-cart-line]", sourceSurface).find(function (candidate) {
        return cartLineIdentity(candidate) === (lock.resolved || identity);
      });
      var nextFocus = renderedLine && one("[data-cart-action='" + action + "']", renderedLine);
      if (!nextFocus) nextFocus = one("[data-cart-quantity], [data-cart-remove], a, button", sourceSurface);
      if (focusOwned && nextFocus) nextFocus.focus({ preventScroll: true });
      announce(result.quantity === 0 ? "Removed from your bag." : "Bag updated.");
      track("cart_change", { quantity: result.quantity });
    }).catch(function (error) {
      setCommerceNotice(errorMessage(error, "We could not confirm that update. Refresh your bag before trying again."));
      if (sourceSurface && sourceSurface.isConnected) announce(errorMessage(error, "Review your bag before trying again."));
    }).finally(function () {
      pendingCartGroups.delete(identity);
      if (lock.resolved) pendingCartGroups.delete(lock.resolved);
      pendingCartLocks.delete(lock);
      applyCartState();
    });
  });

  /* Product cards and quick add. */
  document.addEventListener("click", function (event) {
    var direct = event.target.closest("[data-card-add]");
    if (!direct) return;
    event.preventDefault();
    if (direct.disabled || direct.dataset.pending === "true") return;
    direct.dataset.pending = "true";
    var card = direct.closest("[data-product-card]");
    all("[data-card-add]", card).forEach(function (button) {
      button.dataset.originalDisabled = button.disabled ? "true" : "false";
      button.disabled = true;
    });
    var directProperties = {};
    var directClub = card && card.getAttribute("data-club-label");
    if (directClub) directProperties._gdk_club = directClub;
    var directSeason = card && card.getAttribute("data-season");
    if (directSeason) directProperties._gdk_season = directSeason;
    var directKitType = card && card.getAttribute("data-kit-type");
    if (directKitType) directProperties._gdk_kit_type = directKitType;
    addPairedKitProperties(directProperties, pairedKitFromCard(card));
    var directOverlayVersion = OverlayController.version();
    if (card && card.getAttribute("data-personalisable") === "true") {
      directProperties._gdk_personalisable = "true";
      directProperties._gdk_personalise_url = (card.getAttribute("data-product-url") || "") + "#personalisation";
      var directPersonalisationPrice = Number(card.getAttribute("data-personalisation-price") || 0);
      if (directPersonalisationPrice > 0) directProperties._gdk_personalisation_price = String(directPersonalisationPrice);
    }
    addItemsAndRefresh([{ id: Number(direct.getAttribute("data-card-add")), quantity: 1, properties: directProperties }])
      .then(function (result) {
        track("add_to_cart", { source: "product_card" });
        if (!direct.isConnected) return;
        if (result.reconciled) announce(outcomeMessage(result));
        else purchaseFeedback(card, outcomeMessage(result), true);
        if (OverlayController.version() === directOverlayVersion && !OverlayController.active) OverlayController.open("cart", direct);
      })
      .catch(function (error) {
        if (direct.isConnected) purchaseFeedback(card, errorMessage(error, "We could not complete the add. Your size is saved."), error.recover);
      })
      .finally(function () {
        direct.dataset.pending = "false";
        all("[data-card-add]", card).forEach(function (button) {
          button.disabled = button.dataset.originalDisabled === "true";
          delete button.dataset.originalDisabled;
        });
      });
  });

  var quickAddController;
  var quickAddOrigin;

  function clearQuickAddPresentation() {
    quickAddOrigin = null;
  }

  function normaliseOptions(product) {
    return (product.options || []).map(function (option, index) {
      var name = typeof option === "string" ? option : option.name;
      var values = typeof option === "string" ? [] : (option.values || []);
      if (!values.length) {
        values = Array.from(new Set((product.variants || []).map(function (variant) { return variant.options[index]; })));
      }
      return { name: name, values: values, index: index };
    });
  }

  function personalisationOptionInfo(options) {
    var option = (options || []).find(function (candidate) { return /personali[sz]ation/i.test(candidate.name || ""); });
    if (!option) return null;
    var offValue = option.values.find(function (value) { return /^(no\s+personali[sz]ation|none|no|without(?:\s+personali[sz]ation)?|plain)$/i.test(String(value).trim()); }) || null;
    var addValue = option.values.find(function (value) { return /add\s+name|name\s*[&+]\s*number|name\s+and\s+number/i.test(String(value)); }) || (option.values.length > 1 ? option.values[option.values.length - 1] : null);
    return { index: option.index, offValue: offValue, addValue: addValue };
  }

  function buildQuickAdd(product, sourceButton) {
    var target = one("[data-quick-add-body]");
    var titleTarget = one("[data-quick-add-title]");
    if (!target) return;
    target.replaceChildren();
    if (titleTarget) titleTarget.textContent = "Choose your size";

    var quickCard = sourceButton.closest("[data-product-card]");
    var productPageUrl = sourceButton.getAttribute("data-product-url") || (quickCard && quickCard.getAttribute("data-product-url")) || product.url || "";
    var clubLabel = quickCard && quickCard.getAttribute("data-club-label");
    var seasonLabel = quickCard && quickCard.getAttribute("data-season");
    var kitTypeLabel = quickCard && quickCard.getAttribute("data-kit-type");
    var wrapper = create("div", "gdk-quick-add");
    var productHeader = create("div", "gdk-quick-add__product");
    var imageUrl = product.featured_image || (product.images && product.images[0]);
    if (imageUrl) {
      var image = create("img");
      setCommerceImage(image, imageUrl, [160, 240, 320], "90px");
      image.alt = product.title || "";
      image.loading = "eager";
      productHeader.appendChild(image);
    }
    var copy = create("div");
    var metaBits = [clubLabel, kitTypeLabel, seasonLabel].filter(Boolean);
    if (metaBits.length) copy.appendChild(create("p", "gdk-quick-add__meta", metaBits.join(" · ")));
    copy.appendChild(create("h3", "", product.title));
    var priceRow = create("div", "gdk-quick-add__price gdk-quick-add__price-row");
    var price = create("strong", "", formatMoney(product.price));
    price.setAttribute("data-quick-price", "");
    var compare = create("s", "gdk-quick-add__compare");
    compare.hidden = true;
    var saving = create("span", "gdk-quick-add__saving");
    saving.hidden = true;
    priceRow.append(price, compare, saving);
    copy.appendChild(priceRow);
    var availability = create("p", "gdk-quick-add__availability", "Choose a size to confirm availability");
    copy.appendChild(availability);
    productHeader.appendChild(copy);
    wrapper.appendChild(productHeader);

    var options = normaliseOptions(product);
    var personalisationInfo = personalisationOptionInfo(options);
    var requiresProductDetails = Boolean(personalisationInfo && !personalisationInfo.offValue);
    var visibleOptions = options.filter(function (option) {
      return !/personali[sz]ation/i.test(option.name || "") &&
        !(option.values.length === 1 && option.values[0] === "Default Title");
    });
    var selected = options.map(function () { return null; });
    if (personalisationInfo && personalisationInfo.offValue) selected[personalisationInfo.index] = personalisationInfo.offValue;
    var pending = false;
    var hasAvailableVariants = (product.variants || []).some(function (variant) { return variant.available; });
    var optionRoot = create("div", "gdk-quick-add__options");
    var addButton = create("button", "gdk-button gdk-button--dark gdk-button--large gdk-button--full", "Select size");
    addButton.type = "button";
    addButton.disabled = true;

    function availableFor(index, value) {
      if (requiresProductDetails) return false;
      return (product.variants || []).some(function (variant) {
        return variant.available && variant.options[index] === value && selected.every(function (choice, optionIndex) {
          return optionIndex === index || choice === null || variant.options[optionIndex] === choice;
        });
      });
    }

    function selectedVariant() {
      if (selected.some(function (value) { return value === null; })) return null;
      return (product.variants || []).find(function (variant) {
        return variant.options.every(function (value, index) { return value === selected[index]; });
      });
    }

    function renderState() {
      all("[data-qa-option]", optionRoot).forEach(function (button) {
        var index = Number(button.getAttribute("data-option-index"));
        var value = button.getAttribute("data-option-value");
        button.setAttribute("aria-pressed", selected[index] === value ? "true" : "false");
        button.disabled = pending || !availableFor(index, value);
      });
      optionRoot.classList.toggle("is-pending", pending);
      optionRoot.setAttribute("aria-busy", pending ? "true" : "false");
      var variant = selectedVariant();
      if (variant) {
        price.textContent = formatMoney(variant.price);
        var compareAt = Number(variant.compare_at_price || variant.compareAtPrice || 0);
        compare.hidden = !(compareAt > Number(variant.price));
        compare.textContent = compare.hidden ? "" : formatMoney(compareAt);
        saving.hidden = compare.hidden;
        saving.textContent = compare.hidden ? "" : "Save " + formatMoney(compareAt - Number(variant.price));
        addButton.disabled = pending || !variant.available;
        addButton.textContent = pending ? "Adding to bag…" : (variant.available ? "Add to bag — " + formatMoney(variant.price) : "Sold out");
        availability.textContent = variant.available ? "In stock" : "Sold out";
        availability.dataset.state = variant.available ? "available" : "sold-out";
        addButton.dataset.variantId = String(variant.id);
      } else {
        var sizeOption = visibleOptions.find(function (option) { return /size/i.test(option.name || ""); });
        addButton.disabled = true;
        addButton.textContent = requiresProductDetails ? "Select on product page" : (!hasAvailableVariants ? "Sold out" : (sizeOption && selected[sizeOption.index] === null ? "Select size" : "Choose options"));
        availability.textContent = requiresProductDetails ? "Choose personalisation on the product page." : (hasAvailableVariants ? "Choose a size to confirm availability" : "Sold out");
        availability.dataset.state = hasAvailableVariants ? "pending" : "sold-out";
        compare.hidden = true;
        saving.hidden = true;
        delete addButton.dataset.variantId;
      }
    }

    var defaultOnly = visibleOptions.length === 0 && options.length === 1 && options[0].values.length === 1 && options[0].values[0] === "Default Title";
    if (defaultOnly) selected[0] = "Default Title";
    visibleOptions.forEach(function (option) {
      var optionIndex = option.index;
      var fieldset = create("fieldset", "gdk-option");
      var legend = create("legend", "", /size/i.test(option.name || "") ? "Size" : option.name);
      fieldset.appendChild(legend);
      var values = create("div", "gdk-option__values");
      option.values.forEach(function (value) {
        var button = create("button", "", value);
        button.type = "button";
        button.setAttribute("data-qa-option", "");
        button.setAttribute("data-option-index", String(optionIndex));
        button.setAttribute("data-option-value", value);
        button.setAttribute("aria-pressed", "false");
        button.addEventListener("click", function () {
          if (pending || button.disabled) return;
          selected[optionIndex] = value;
          var compatible = (product.variants || []).some(function (variant) {
            return variant.available && selected.every(function (choice, index) { return choice === null || variant.options[index] === choice; });
          });
          if (!compatible) {
            selected = selected.map(function (choice, index) {
              if (index === optionIndex) return choice;
              if (personalisationInfo && index === personalisationInfo.index) return personalisationInfo.offValue;
              return null;
            });
            announce("That combination changed. Please review the remaining options.");
          }
          renderState();
        });
        values.appendChild(button);
      });
      fieldset.appendChild(values);
      optionRoot.appendChild(fieldset);
    });
    if (visibleOptions.length) wrapper.appendChild(optionRoot);

    var supportLinks = create("nav", "gdk-quick-add__links");
    supportLinks.setAttribute("aria-label", "Product details");
    if (productPageUrl) {
      var detailsLink = create("a", "", "View full details");
      detailsLink.href = productPageUrl;
      supportLinks.appendChild(detailsLink);
    }
    addButton.addEventListener("click", function () {
      var variantId = Number(addButton.dataset.variantId || 0);
      if (!variantId || pending || addButton.disabled) return;
      pending = true;
      renderState();
      var quickProperties = {};
      var quickClub = quickCard && quickCard.getAttribute("data-club-label");
      if (quickClub) quickProperties._gdk_club = quickClub;
      if (seasonLabel) quickProperties._gdk_season = seasonLabel;
      var quickKitType = quickCard && quickCard.getAttribute("data-kit-type");
      if (quickKitType) quickProperties._gdk_kit_type = quickKitType;
      addPairedKitProperties(quickProperties, pairedKitFromCard(quickCard));
      var submittedOverlayVersion = OverlayController.version();
      addItemsAndRefresh([{ id: variantId, quantity: 1, properties: quickProperties }])
        .then(function (result) {
          track("add_to_cart", { source: "quick_add", product_id: product.id, variant_id: variantId });
          if (!wrapper.isConnected || !sourceButton.isConnected || submittedOverlayVersion !== OverlayController.version()) return;
          if (result.reconciled) announce(outcomeMessage(result));
          else purchaseFeedback(wrapper, outcomeMessage(result), true);
          OverlayController.open("cart", sourceButton);
        })
        .catch(function (error) {
          if (wrapper.isConnected && sourceButton.isConnected && submittedOverlayVersion === OverlayController.version()) {
            purchaseFeedback(wrapper, errorMessage(error, "We could not complete the add. Your size is saved."), error.recover);
          }
        })
        .finally(function () {
          pending = false;
          if (!wrapper.isConnected) return;
          addButton.classList.remove("is-loading", "is-added");
          renderState();
        });
    });
    wrapper.appendChild(addButton);
    if (supportLinks.children.length) wrapper.appendChild(supportLinks);
    target.appendChild(wrapper);
    target.setAttribute("aria-busy", "false");
    renderState();
  }

  document.addEventListener("click", function (event) {
    var button = event.target.closest("[data-quick-add]");
    if (!button) return;
    event.preventDefault();
    var productUrl = button.getAttribute("data-product-url");
    if (!productUrl) return;
    clearQuickAddPresentation();
    quickAddOrigin = button;
    if (quickAddController) quickAddController.abort();
    quickAddController = new AbortController();
    var requestController = quickAddController;
    var target = one("[data-quick-add-body]");
    if (target) {
      target.replaceChildren();
      target.setAttribute("aria-busy", "true");
      var loading = create("div", "gdk-loading");
      loading.setAttribute("aria-label", "Loading");
      loading.append(create("span"), create("span"), create("span"));
      target.appendChild(loading);
    }
    OverlayController.open("quick-add", button);
    var url = new URL(productUrl, window.location.origin);
    if (!/\.js$/.test(url.pathname)) url.pathname += ".js";
    fetchJSON(url.toString(), { signal: requestController.signal, headers: { Accept: "application/json" } })
      .then(function (product) {
        if (quickAddController !== requestController || !OverlayController.active || OverlayController.active.getAttribute("data-overlay") !== "quick-add") return;
        buildQuickAdd(product, button);
      })
      .catch(function (error) {
        if (error.name === "AbortError") return;
        if (quickAddController !== requestController) return;
        if (target) {
          target.setAttribute("aria-busy", "false");
          var failure = create("div", "gdk-quick-add__error");
          failure.appendChild(create("p", "gdk-form-error", errorMessage(error, "Product options are unavailable. Open the product page to continue.")));
          var fullLink = create("a", "gdk-button gdk-button--secondary gdk-button--full", "View product");
          fullLink.href = productUrl;
          failure.appendChild(fullLink);
          target.replaceChildren(failure);
        }
      });
  });

  document.addEventListener("gdk:overlay:close", function (event) {
    if (!event.detail || event.detail.name !== "quick-add") return;
    clearQuickAddPresentation();
    if (quickAddController) quickAddController.abort();
    quickAddController = null;
  });

  /* Product detail: gallery, deliberate variants, grouped personalisation and sticky purchase. */
  /* A short-lived draft is an editable suggestion, never a variant or purchase. */
  var personalisationDraftKey = "gdk.personalisationDraft.v1";
  var personalisationDraftLifetime = 30 * 60 * 1000;

  function draftProductPath(value) {
    try {
      var url = new URL(value, window.location.origin);
      return url.origin === window.location.origin && /\/products\/[^/]+\/?$/.test(url.pathname)
        ? url.pathname.replace(/\/$/, "") : "";
    } catch (error) { return ""; }
  }

  function clearPersonalisationDraft() {
    try { window.sessionStorage.removeItem(personalisationDraftKey); } catch (error) {}
  }

  function readPersonalisationDraft() {
    try {
      var value = JSON.parse(window.sessionStorage.getItem(personalisationDraftKey) || "null");
      if (!value) return null;
      var age = Date.now() - Number(value.createdAt);
      if (value.version !== 1 || !Number.isFinite(age) || age < 0 || age > personalisationDraftLifetime ||
          !/^\d+$/.test(String(value.productId || "")) || !draftProductPath(value.url) ||
          typeof value.name !== "string" || typeof value.number !== "string" ||
          value.name.length > 80 || value.number.length > 8) {
        clearPersonalisationDraft();
        return null;
      }
      return value;
    } catch (error) { clearPersonalisationDraft(); return null; }
  }

  function initProduct(root) {
    componentElements("[data-product-root]", root).forEach(function (productRoot) {
      var life = componentScope(productRoot, "product");
      if (!life) return;
      var dataNode = one("[data-product-json]", productRoot);
      var form = one("[data-gdk-product-form]", productRoot);
      if (!dataNode || !form) { life.dispose(); return; }
      var product;
      try { product = JSON.parse(dataNode.textContent); } catch (error) { life.dispose(); return; }
      var originalNoValidate = form.noValidate;
      form.noValidate = true;
      life.add(function () { form.noValidate = originalNoValidate; });
      var hiddenId = one("[data-variant-id]", form);
      var noJsSelect = one(".gdk-no-js select", form);
      if (noJsSelect) noJsSelect.disabled = true;
      if (hiddenId) hiddenId.name = "id";
      var optionGroups = all("[data-option-index]", form);
      var selected = (product.options || []).map(function () { return null; });
      var addButton = one("[data-add-to-cart]", form);
      var addLabel = one("[data-add-label]", form);
      var stickyPurchase = one("[data-sticky-purchase]", productRoot);
      var stickyButton = one("[data-sticky-add]", productRoot);
      var stickyLabel = one("[data-sticky-label]", productRoot);
      var price = one("[data-product-price]", productRoot);
      var compare = one("[data-product-compare]", productRoot);
      var stickyPrice = one("[data-sticky-price]", productRoot);
      var dynamicCheckout = one("[data-dynamic-checkout]", form);
      var errorBox = one("[data-product-error]", form);
      var inventoryState = one("[data-inventory-state]", form);
      var purchaseRecovery = false;
      life.on(document, "gdk:cart:reconciled", function () {
        if (purchaseRecovery) { purchaseRecovery = false; renderVariant(true); }
      });
      var personalisation = one("[data-personalisation]", form);
      var personalisationToggle = personalisation && one("[data-personalisation-toggle]", personalisation);
      var personalisationOff = personalisation && one("[data-personalisation-off]", personalisation);
      var personalisationContent = personalisation && one("[data-personalisation-content]", personalisation);
      var personalisationName = personalisation && one("[data-personalisation-name]", personalisation);
      var personalisationNumber = personalisation && one("[data-personalisation-number]", personalisation);
      var personalisationConfirmation = personalisation && one("[data-personalisation-confirmation]", personalisation);
      var personalisationShirtPrice = personalisation && one("[data-personalisation-shirt-price]", personalisation);
      var personalisationPrintingPrice = personalisation && one("[data-personalisation-printing-price]", personalisation);
      var personalisationTotal = personalisation && one("[data-personalisation-total]", personalisation);
      var personalisationChoicePrice = personalisation && one("[data-personalisation-choice-price]", personalisation);
      var shirtSummary = personalisation && one("[data-shirt-summary]", personalisation);
      var summarySize = personalisation && one("[data-summary-size]", personalisation);
      var summaryPrint = personalisation && one("[data-summary-personalisation]", personalisation);
      var draftNotice = personalisation && one("[data-personalisation-draft-notice]", personalisation);
      var previewImage = personalisation && one("[data-personalisation-preview-image]", personalisation);
      var previewUnavailable = personalisation && one("[data-preview-unavailable]", personalisation);
      var previewSurface = personalisation && one("[data-personalisation-preview]", personalisation);
      if (personalisation) applyPersonalisationStyle(personalisation, personalisation);
      var previewStarted = false;
      var personalisationMode = personalisation ? (personalisation.getAttribute("data-personalisation-mode") || "service") : "service";
      var personalisationOptionIndex = Number(product.personalisationOptionIndex);
      if (!Number.isInteger(personalisationOptionIndex)) {
        personalisationOptionIndex = personalisation ? Number(personalisation.getAttribute("data-personalisation-option-index") || -1) : -1;
      }
      var personalisationOffValue = product.personalisationOffValue || (personalisation && personalisation.getAttribute("data-personalisation-off-value"));
      var personalisationAddValue = product.personalisationAddValue || (personalisation && personalisation.getAttribute("data-personalisation-add-value"));
      var missingPlainVariant = personalisationOptionIndex >= 0 && !personalisationOffValue;
      var pending = false;
      var hasAvailableVariants = product.variants.some(function (variant) { return variant.available; });
      var currentVariant = product.hasOnlyDefaultVariant ? product.variants[0] : null;
      var personalisationExpanded = false;
      var syncStickyVisibility = function () {};
      if (!product.hasOnlyDefaultVariant && Number(product.selectedVariantId || 0)) {
        var linkedVariant = product.variants.find(function (variant) { return Number(variant.id) === Number(product.selectedVariantId); });
        if (linkedVariant) selected = linkedVariant.options.slice();
      }
      if (personalisationOptionIndex >= 0) {
        selected[personalisationOptionIndex] = personalisationOffValue || null;
      }

      function renderInventory(variant) {
        if (!inventoryState || config.showInventoryStatus === false) {
          if (inventoryState) inventoryState.hidden = true;
          return;
        }
        inventoryState.hidden = false;
        if (!variant) {
          inventoryState.textContent = missingPlainVariant ? selectionErrorMessage() : (hasAvailableVariants ? "Select a size to see availability" : "Sold out");
          inventoryState.dataset.state = hasAvailableVariants ? "pending" : "sold-out";
          return;
        }
        if (!variant.available) {
          inventoryState.textContent = "Sold out";
          inventoryState.dataset.state = "sold-out";
          return;
        }
        var tracked = Boolean(variant.inventoryManagement) && variant.inventoryPolicy === "deny";
        var quantity = Number(variant.inventoryQuantity || 0);
        var threshold = Number(config.lowStockThreshold || 3);
        if (tracked && quantity > 0 && quantity <= threshold) {
          var sizeIndex = (product.options || []).findIndex(function (name) { return /size/i.test(name); });
          var sizeSuffix = sizeIndex >= 0 && variant.options[sizeIndex] ? " in " + variant.options[sizeIndex] : "";
          inventoryState.textContent = "Only " + quantity + " left" + sizeSuffix;
          inventoryState.dataset.state = "low";
        } else {
          inventoryState.textContent = "In stock";
          inventoryState.dataset.state = "available";
        }
      }

      function availableFor(index, value) {
        return product.variants.some(function (variant) {
          return variant.available && variant.options[index] === value && selected.every(function (choice, optionIndex) {
            return optionIndex === index || choice === null || variant.options[optionIndex] === choice;
          });
        });
      }

      function findSelectedVariant() {
        if (missingPlainVariant) return null;
        if (selected.some(function (choice) { return choice === null; })) return null;
        return product.variants.find(function (variant) {
          return variant.options.every(function (value, index) { return value === selected[index]; });
        }) || null;
      }

      function missingLabel() {
        if (missingPlainVariant) return "Options unavailable";
        var sizeIndex = (product.options || []).findIndex(function (name) { return /size/i.test(name); });
        if (sizeIndex >= 0 && selected[sizeIndex] === null) return "Select size";
        return "Choose options";
      }

      function selectionErrorMessage() {
        if (missingPlainVariant) return "This shirt's options are temporarily unavailable. Please contact support.";
        return product.hasSizeOption ? "Select your size to continue." : "Choose all available options to continue.";
      }

      function showSelectionError() {
        var firstMissing = optionGroups.find(function (group) {
          var index = Number(group.getAttribute("data-option-index"));
          return selected[index] === null;
        });
        var message = selectionErrorMessage();
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.textContent = message;
        }
        (firstMissing || form).scrollIntoView({ behavior: !Motion.enabled ? "auto" : "smooth", block: "center" });
        var firstButton = firstMissing && one("button:not([disabled])", firstMissing);
        if (firstButton) firstButton.focus({ preventScroll: true });
        if (!errorBox) announce(message);
      }

      function isPersonalisationEnabled() {
        return Boolean(personalisationToggle && personalisationToggle.checked);
      }

      function variantForPersonalisationValue(value) {
        if (personalisationOptionIndex < 0 || !value) return null;
        var desired = selected.slice();
        desired[personalisationOptionIndex] = value;
        if (desired.some(function (choice) { return choice === null; })) return null;
        return product.variants.find(function (variant) {
          return variant.options.every(function (choice, index) { return choice === desired[index]; });
        }) || null;
      }

      function renderPersonalisationSummary() {
        if (!personalisation) return;
        var hasSelectedPrice = Boolean(currentVariant);
        var shirtPrice = hasSelectedPrice ? Number(currentVariant.price || 0) : 0;
        var printingPrice = Number(personalisation.getAttribute("data-service-price") || 0);
        var totalPrice = shirtPrice;
        if (personalisationMode === "variant") {
          var baseVariant = variantForPersonalisationValue(personalisationOffValue);
          var addVariant = variantForPersonalisationValue(personalisationAddValue);
          if (baseVariant) shirtPrice = Number(baseVariant.price || 0);
          if (baseVariant && addVariant) printingPrice = Math.max(0, Number(addVariant.price || 0) - Number(baseVariant.price || 0));
          totalPrice = isPersonalisationEnabled() && addVariant ? Number(addVariant.price || 0) : shirtPrice;
        } else {
          totalPrice = shirtPrice + (isPersonalisationEnabled() ? printingPrice : 0);
        }
        if (personalisationChoicePrice) personalisationChoicePrice.textContent = printingPrice > 0 ? "+" + formatMoney(printingPrice) : "";
        if (personalisationShirtPrice) personalisationShirtPrice.textContent = hasSelectedPrice ? formatMoney(shirtPrice) : "Select a size";
        if (personalisationPrintingPrice) personalisationPrintingPrice.textContent = printingPrice > 0 ? formatMoney(printingPrice) : "Included";
        if (personalisationTotal) personalisationTotal.textContent = hasSelectedPrice ? formatMoney(totalPrice) : "Select a size";
        var value = personalisationValue(personalisationName, personalisationNumber, true);
        var serviceAvailable = personalisationMode === "variant" || (
          Number(personalisation.getAttribute("data-service-variant")) > 0 &&
          personalisation.getAttribute("data-service-available") !== "false"
        );
        var composing = [personalisationName, personalisationNumber].some(function (field) {
          return field && field.dataset.gdkComposing === "true";
        });
        var ready = isPersonalisationEnabled() && value.valid && !composing &&
          Boolean(currentVariant && currentVariant.available) && serviceAvailable;
        personalisation.dataset.configurationState = !isPersonalisationEnabled() ? "off" : ready ? "ready" : "editing";
        if (shirtSummary) shirtSummary.hidden = !ready;
        if (summarySize) {
          var sizeIndex = (product.options || []).findIndex(function (name) { return /size/i.test(name); });
          summarySize.textContent = sizeIndex >= 0 && selected[sizeIndex] ? "Size " + selected[sizeIndex] : "Selected options";
        }
        if (summaryPrint) summaryPrint.textContent = value.name + (value.name && value.number ? " · " : "") + value.number;
        if (personalisationConfirmation) personalisationConfirmation.hidden = !ready;
        if (stickyPrice && hasSelectedPrice) stickyPrice.textContent = formatMoney(totalPrice);
        if (stickyLabel) {
          stickyLabel.textContent = pending ? "Adding to bag…" : purchaseRecovery ? "Check bag" :
            !hasAvailableVariants || (currentVariant && !currentVariant.available) ? "Sold out" :
            !currentVariant ? missingLabel() :
            isPersonalisationEnabled() && !ready ? "Finish personalisation" : "Add to bag";
        }
      }

      function loadPersonalisationPreview() {
        if (!previewImage || previewStarted) return;
        var source = previewImage.getAttribute("data-preview-src");
        if (!source) return;
        previewStarted = true;
        var print = previewSurface && one(".gdk-shirt-preview__print", previewSurface);
        if (previewSurface) previewSurface.setAttribute("aria-busy", "true");
        life.on(previewImage, "load", function () {
          if (previewSurface) previewSurface.removeAttribute("aria-busy");
          previewImage.hidden = false;
          if (print) print.hidden = false;
          if (previewUnavailable) previewUnavailable.hidden = true;
        }, { once: true });
        life.on(previewImage, "error", function () {
          if (previewSurface) previewSurface.removeAttribute("aria-busy");
          previewImage.hidden = true;
          if (print) print.hidden = true;
          if (previewUnavailable) previewUnavailable.hidden = false;
        }, { once: true });
        var srcset = previewImage.getAttribute("data-preview-srcset");
        if (srcset) previewImage.srcset = srcset;
        previewImage.src = source;
        previewImage.hidden = false;
      }

      function setPersonalisationState(focusEditor) {
        if (!personalisation || !personalisationToggle || !personalisationContent) return;
        personalisationExpanded = isPersonalisationEnabled();
        if (personalisationMode === "variant" && personalisationOptionIndex >= 0) {
          selected[personalisationOptionIndex] = personalisationExpanded ? personalisationAddValue : personalisationOffValue;
          renderVariant(true);
        }
        document.body.classList.toggle("gdk-personalisation-open", personalisationExpanded);
        personalisationContent.hidden = !personalisationExpanded;
        personalisationContent.setAttribute("aria-hidden", personalisationExpanded ? "false" : "true");
        personalisation.classList.toggle("is-active", personalisationExpanded);
        personalisationToggle.setAttribute("aria-expanded", personalisationExpanded ? "true" : "false");
        [personalisationName, personalisationNumber].forEach(function (field) {
          if (!field) return;
          field.required = personalisationExpanded;
          field.setAttribute("aria-required", personalisationExpanded ? "true" : "false");
          if (!personalisationExpanded) field.removeAttribute("aria-invalid");
        });
        if (dynamicCheckout) dynamicCheckout.hidden = personalisationExpanded || !currentVariant || !currentVariant.available;
        renderPersonalisationSummary();
        syncStickyVisibility();
        if (personalisationExpanded) loadPersonalisationPreview();
        if (personalisationExpanded && focusEditor && personalisationName) {
          life.frame(function () {
            personalisationContent.scrollIntoView({ behavior: !Motion.enabled ? "auto" : "smooth", block: "nearest" });
            personalisationName.focus({ preventScroll: true });
          });
        }
      }

      function showMedia(mediaId) {
        if (!mediaId) return;
        var gallery = one("[data-product-gallery]", productRoot);
        var trackElement = gallery && one("[data-gallery-track]", gallery);
        var item = all("[data-gallery-item]", productRoot).find(function (media) {
          return Number(media.getAttribute("data-media-id")) === Number(mediaId);
        });
        if (item && trackElement && getComputedStyle(trackElement).overflowX !== "visible") {
          trackElement.scrollTo({ left: item.offsetLeft, behavior: !Motion.enabled ? "auto" : "smooth" });
        }
      }

      function setPurchasePending(isPending) {
        pending = isPending;
        if (isPending) {
          addLabel.textContent = "Adding to bag…";
          if (stickyLabel) stickyLabel.textContent = "Adding to bag…";
        }
        form.setAttribute("aria-busy", isPending ? "true" : "false");
        form.classList.toggle("is-pending", isPending);
        if (addButton) addButton.disabled = isPending || (!currentVariant ? !hasAvailableVariants : !currentVariant.available);
        if (stickyButton) stickyButton.disabled = isPending || (!currentVariant ? !hasAvailableVariants : !currentVariant.available);
        [personalisationToggle, personalisationOff, personalisationName, personalisationNumber].forEach(function (control) {
          if (control) control.disabled = isPending;
        });
      }

      function renderVariant(skipMedia) {
        optionGroups.forEach(function (group) {
          var index = Number(group.getAttribute("data-option-index"));
          var selectedText = one("[data-selected-option]", group);
          if (selectedText) selectedText.textContent = selected[index] || "";
          all("[data-option-value]", group).forEach(function (button) {
            var value = button.getAttribute("data-option-value");
            button.setAttribute("aria-pressed", selected[index] === value ? "true" : "false");
            button.disabled = pending || !availableFor(index, value);
          });
        });
        currentVariant = product.hasOnlyDefaultVariant ? product.variants[0] : findSelectedVariant();
        if (currentVariant) {
          hiddenId.value = currentVariant.id;
          hiddenId.dispatchEvent(new Event("change", { bubbles: true }));
          price.textContent = formatMoney(currentVariant.price);
          if (stickyPrice) stickyPrice.textContent = formatMoney(currentVariant.price);
          if (compare) {
            compare.hidden = !(currentVariant.compareAtPrice > currentVariant.price);
            compare.textContent = compare.hidden ? "" : formatMoney(currentVariant.compareAtPrice);
          }
          var available = Boolean(currentVariant.available);
          addButton.disabled = !available;
          if (stickyButton) stickyButton.disabled = !available;
          addLabel.textContent = available ? "Add to bag" : "Sold out";
          if (stickyLabel) stickyLabel.textContent = available ? "Add to bag" : "Sold out";
          if (dynamicCheckout) dynamicCheckout.hidden = !available || isPersonalisationEnabled();
          if (available) {
            var url = new URL(window.location.href);
            url.searchParams.set("variant", currentVariant.id);
            window.history.replaceState({}, "", url.toString());
          }
          if (!skipMedia) showMedia(currentVariant.featuredMediaId);
          renderInventory(currentVariant);
        } else {
          hiddenId.value = "";
          hiddenId.dispatchEvent(new Event("change", { bubbles: true }));
          var basePrice = Math.min.apply(null, product.variants.map(function (variant) { return variant.price; }));
          if (Number.isFinite(basePrice)) {
            price.textContent = formatMoney(basePrice);
            if (stickyPrice) stickyPrice.textContent = formatMoney(basePrice);
          }
          if (compare) { compare.hidden = true; compare.textContent = ""; }
          addButton.disabled = !hasAvailableVariants;
          if (stickyButton) stickyButton.disabled = !hasAvailableVariants;
          addLabel.textContent = hasAvailableVariants ? missingLabel() : "Sold out";
          if (stickyLabel) stickyLabel.textContent = hasAvailableVariants ? missingLabel() : "Sold out";
          if (dynamicCheckout) dynamicCheckout.hidden = true;
          var cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete("variant");
          window.history.replaceState({}, "", cleanUrl.toString());
          renderInventory(null);
        }
        renderPersonalisationSummary();
        if (pending) {
          addButton.disabled = true;
          if (stickyButton) stickyButton.disabled = true;
        }
        if (purchaseRecovery) {
          addLabel.textContent = "Check bag";
          if (stickyLabel) stickyLabel.textContent = "Check bag";
        }
      }

      optionGroups.forEach(function (group) {
        var index = Number(group.getAttribute("data-option-index"));
        all("[data-option-value]", group).forEach(function (button) {
          life.on(button, "click", function () {
            if (pending || button.disabled) return;
            selected[index] = button.getAttribute("data-option-value");
            var compatible = product.variants.some(function (variant) {
              return variant.available && selected.every(function (choice, optionIndex) {
                return choice === null || variant.options[optionIndex] === choice;
              });
            });
            if (!compatible) {
              selected = selected.map(function (choice, optionIndex) {
                if (optionIndex === index) return choice;
                if (optionIndex === personalisationOptionIndex && personalisationOffValue) return personalisationOffValue;
                return null;
              });
              announce("That combination changed. Please review the remaining options.");
            }
            if (errorBox) errorBox.hidden = true;
            renderVariant();
          });
        });
      });

      if (personalisation) {
        var toggle = personalisationToggle;
        var content = personalisationContent;
        var nameInput = personalisationName;
        var numberInput = personalisationNumber;
        var previewName = one("[data-preview-name]", personalisation);
        var previewNumber = one("[data-preview-number]", personalisation);
        function syncPersonalisationFromChoice(event) {
          if (pending) return;
          if (event && event.target && event.target.matches("input[type='radio']")) {
            life.frame(function () { setPersonalisationState(Boolean(toggle.checked)); });
          } else {
            setPersonalisationState(Boolean(toggle.checked));
          }
        }
        life.on(toggle, "change", syncPersonalisationFromChoice);
        if (personalisationOff) {
          life.on(personalisationOff, "change", syncPersonalisationFromChoice);
        }
        function renderPersonalisationPreview() {
          var previewValue = personalisationValue(nameInput, numberInput, false);
          var nameValue = previewValue.name;
          var numberValue = previewValue.number;
          var hasCustomerInput = Boolean(nameValue || numberValue);
          if (previewName) {
            previewName.textContent = hasCustomerInput ? nameValue : "YOUR NAME";
            previewName.hidden = hasCustomerInput && !nameValue;
          }
          if (previewNumber) {
            previewNumber.textContent = hasCustomerInput ? numberValue : "00";
            previewNumber.hidden = hasCustomerInput && !numberValue;
          }
        }
        var personalisationError = one("[data-personalisation-feedback]", personalisation);
        bindPersonalisationInputs(life, nameInput, numberInput, personalisationError, function () {
          renderPersonalisationPreview();
          renderPersonalisationSummary();
        });
        [nameInput, numberInput].forEach(function (field) {
          if (field) life.on(field, "compositionstart", renderPersonalisationSummary);
        });
        var draft = readPersonalisationDraft();
        if (draft && String(draft.productId) === String(product.id) &&
            draftProductPath(draft.url) === draftProductPath(product.url) &&
            !nameInput.value && !numberInput.value &&
            Number(personalisation.getAttribute("data-service-variant")) > 0) {
          var draftValue = personalisationValue({ value: draft.name }, { value: draft.number }, true);
          if (draftValue.valid) {
            nameInput.value = draft.name;
            numberInput.value = draft.number;
            toggle.checked = true;
            if (personalisationOff) personalisationOff.checked = false;
            clearPersonalisationDraft();
            if (draftNotice) draftNotice.hidden = false;
            renderPersonalisationPreview();
            setPersonalisationState(false);
          }
        }
        var clearDraftButton = one("[data-clear-personalisation-draft]", personalisation);
        if (clearDraftButton) life.on(clearDraftButton, "click", function () {
          if (pending) return;
          clearPersonalisationDraft();
          nameInput.value = "";
          numberInput.value = "";
          if (draftNotice) draftNotice.hidden = true;
          personalisationFeedback(nameInput, numberInput, personalisationError, false);
          renderPersonalisationPreview();
          renderPersonalisationSummary();
          nameInput.focus({ preventScroll: true });
        });

        var openPersonalisationFromHash = function (focusEditor) {
          if (String(window.location.hash || "").toLowerCase() !== "#personalisation") return;
          toggle.checked = true;
          if (personalisationOff) personalisationOff.checked = false;
          setPersonalisationState(false);
          life.frame(function () {
            personalisation.scrollIntoView({ behavior: !Motion.enabled ? "auto" : "smooth", block: "start" });
            if (focusEditor && nameInput) nameInput.focus({ preventScroll: true });
          });
        };
        productRoot._gdkOpenPersonalisation = openPersonalisationFromHash;
        openPersonalisationFromHash(false);
      }

      life.on(form, "submit", function (event) {
        event.preventDefault();
        if (pending) return;
        if (purchaseRecovery) {
          var recovery = one("[data-cart-recover]", form);
          if (recovery) recovery.click();
          return;
        }
        if (!currentVariant || !currentVariant.available) {
          showSelectionError();
          return;
        }

        var items = [];
        var properties = {};
        if (product.clubName) properties._gdk_club = product.clubName;
        if (product.season) properties._gdk_season = product.season;
        if (product.kitType) properties._gdk_kit_type = product.kitType;
        addPairedKitProperties(properties, product.pairedKit);
        var serviceItem = null;
        var submittedPersonalised = false;
        if (personalisation) {
          properties._gdk_personalisable = "true";
          properties._gdk_personalise_url = product.url + "#personalisation";
          var personalisationPrice = Number(personalisation.getAttribute("data-service-price") || 0);
          if (personalisationPrice > 0) properties._gdk_personalisation_price = String(personalisationPrice);
          var enabled = Boolean(personalisationToggle && personalisationToggle.checked);
          if (enabled) {
            if (personalisationName.dataset.gdkComposing === "true" || personalisationNumber.dataset.gdkComposing === "true") return;
            var entered = personalisationFeedback(personalisationName, personalisationNumber, one("[data-personalisation-feedback]", personalisation), true);
            if (!entered.valid) {
              if (!one("[data-personalisation-feedback]", personalisation)) purchaseFeedback(form, entered.message, false);
              (entered.nameError ? personalisationName : personalisationNumber).focus({ preventScroll: true });
              return;
            }
            var personalName = entered.name;
            var personalNumber = entered.number;
            properties.Name = personalName;
            properties.Number = personalNumber;
            submittedPersonalised = true;

            if (personalisationMode === "service") {
              var entropy = window.crypto && window.crypto.getRandomValues
                ? window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36)
                : Math.random().toString(36).slice(2, 9);
              var groupId = "gdk_" + product.id + "_" + Date.now().toString(36) + "_" + entropy;
              properties._gdk_group_id = groupId;
              properties._gdk_role = "product";
              var serviceProperties = {
                _gdk_group_id: groupId,
                _gdk_role: "service",
                For: product.title,
                Name: personalName,
                Number: personalNumber
              };
              var serviceVariantId = Number(personalisation.getAttribute("data-service-variant") || 0);
              if (!serviceVariantId) {
                if (errorBox) {
                  errorBox.hidden = false;
                  errorBox.textContent = "Personalisation is temporarily unavailable. Add the shirt without personalisation or contact support.";
                }
                if (!errorBox) announce("Personalisation is temporarily unavailable.");
                return;
              }
              serviceItem = {
                id: serviceVariantId,
                quantity: 1,
                properties: serviceProperties
              };
            } else {
              properties._gdk_personalised_variant = "true";
            }
          }
        }
        var submittedVariantId = Number(currentVariant.id);
        items.push({ id: submittedVariantId, quantity: 1, properties: properties });
        if (serviceItem) items.push(serviceItem);

        setPurchasePending(true);
        if (errorBox) errorBox.hidden = true;
        var submittedOverlayVersion = OverlayController.version();
        addItemsAndRefresh(items)
          .then(function (result) {
            track("add_to_cart", { source: "product_page", product_id: product.id, variant_id: submittedVariantId, personalised: submittedPersonalised });
            if (!life.active) return;
            purchaseRecovery = !result.reconciled;
            if (result.reconciled) announce(outcomeMessage(result));
            else purchaseFeedback(form, outcomeMessage(result), true);
            if (OverlayController.version() === submittedOverlayVersion && !OverlayController.active) OverlayController.open("cart", addButton);
          })
          .catch(function (error) {
            if (!life.active) return;
            purchaseRecovery = Boolean(error.recover);
            purchaseFeedback(form, errorMessage(error, "We could not complete the add. Your selection is saved."), error.recover);
          })
          .finally(function () {
            if (!life.active) return;
            addButton.classList.remove("is-loading", "is-added");
            setPurchasePending(false);
            renderVariant(true);
          });
      });

      if (stickyButton) {
        life.on(stickyButton, "click", function () {
          if (!currentVariant || !currentVariant.available) {
            showSelectionError();
            return;
          }
          form.requestSubmit(addButton);
        });

        if ("IntersectionObserver" in window) {
          var footerVisible = false;
          var addVisible = true;
          var commerceZonesVisible = new Set();
          var applySticky = function () {
            var commerceVisible = commerceZonesVisible.size > 0;
            document.body.classList.toggle("gdk-commerce-zone-visible", commerceVisible);
            var shouldShow = hasAvailableVariants && !addVisible && !footerVisible && !commerceVisible && !personalisationExpanded && !OverlayController.active && !document.body.classList.contains("gdk-keyboard-context");
            stickyPurchase.classList.toggle("is-visible", shouldShow);
            document.body.classList.toggle("gdk-sticky-purchase-active", shouldShow);
            stickyPurchase.setAttribute("aria-hidden", shouldShow ? "false" : "true");
            stickyPurchase.toggleAttribute("inert", !shouldShow);
            stickyButton.tabIndex = shouldShow ? 0 : -1;
          };
          syncStickyVisibility = applySticky;
          life.on(document, "gdk:keyboard-context", applySticky);
          life.on(document, "gdk:overlay:open", applySticky);
          life.on(document, "gdk:overlay:close", applySticky);
          var releaseVisibility = function () {};
          productRoot._gdkRefreshVisibility = function (excludedRoot) {
            releaseVisibility();
            footerVisible = false;
            addVisible = true;
            commerceZonesVisible.clear();
            var eligible = function (node) {
              return node && node.isConnected && !(excludedRoot && (node === excludedRoot || excludedRoot.contains(node)));
            };
            var watching = true;
            var observers = [];
            releaseVisibility = life.add(function () { watching = false; observers.forEach(function (observer) { observer.disconnect(); }); });
            var addObserver = new IntersectionObserver(function (entries) {
              if (!life.active || !watching) return;
              addVisible = entries[0].isIntersecting;
              applySticky();
            }, { threshold: .1 });
            observers.push(addObserver);
            addObserver.observe(addButton);
            var footer = one(".gdk-footer");
            if (eligible(footer)) {
              var footerObserver = new IntersectionObserver(function (entries) {
                if (!life.active || !watching) return;
                footerVisible = entries[0].isIntersecting;
                applySticky();
              }, { threshold: .05 });
              observers.push(footerObserver);
              footerObserver.observe(footer);
            }
            var commerceObserver = new IntersectionObserver(function (entries) {
              if (!life.active || !watching) return;
              entries.forEach(function (entry) {
                if (entry.isIntersecting) commerceZonesVisible.add(entry.target);
                else commerceZonesVisible.delete(entry.target);
              });
              applySticky();
            }, { threshold: .08 });
            observers.push(commerceObserver);
            all(".gdk-kit-pair, product-recommendations").filter(eligible).forEach(function (zone) { commerceObserver.observe(zone); });
            applySticky();
          };
          productRoot._gdkRefreshVisibility();
        }
      }

      life.add(function () {
        delete productRoot._gdkOpenPersonalisation;
        delete productRoot._gdkRefreshVisibility;
        setPurchasePending(false);
        addButton.classList.remove("is-loading", "is-added");
        document.body.classList.remove("gdk-personalisation-open", "gdk-sticky-purchase-active", "gdk-commerce-zone-visible");
        if (stickyPurchase) {
          stickyPurchase.classList.remove("is-visible");
          stickyPurchase.setAttribute("aria-hidden", "true");
          stickyPurchase.setAttribute("inert", "");
        }
      });
      renderVariant();
      track("view_product", { product_id: product.id });
    });
  }

  var inspectionSerial = 0;
  function initGalleries(root) {
    componentElements("[data-product-gallery]", root).forEach(function (gallery) {
      var life = componentScope(gallery, "gallery");
      if (!life) return;
      var trackElement = one("[data-gallery-track]", gallery);
      var items = all("[data-gallery-item]", gallery);
      var counter = one("[data-gallery-current]", gallery);
      var mediaRole = one("[data-gallery-role]", gallery);
      var progressButtons = all("[data-gallery-progress-button]", gallery);
      var progressFill = one("[data-gallery-progress-fill]", gallery);
      var swipeCue = one("[data-gallery-swipe-cue]", gallery);
      var thumbs = all("[data-gallery-thumb]", gallery);
      var ticking = false;
      var interacted = false;
      var inspectionLinks = all("[data-gallery-inspect]", gallery);
      var inspection = null;
      var inspectionIndex = 0;
      var inspectionImage;
      var inspectionCaption;
      var inspectionPrevious;
      var inspectionNext;
      var inspectionError;
      var inspectionName = "inspection-" + (++inspectionSerial);

      function displayNumber(value) {
        return String(value).padStart(2, "0");
      }

      function markInteracted() {
        if (interacted) return;
        interacted = true;
        gallery.classList.add("has-gallery-interacted");
        if (swipeCue) swipeCue.setAttribute("aria-hidden", "true");
      }

      function setActive(index) {
        index = Math.max(0, Math.min(items.length - 1, Number(index) || 0));
        gallery.dataset.galleryIndex = String(index);
        if (counter) counter.textContent = displayNumber(index + 1);
        if (mediaRole) mediaRole.textContent = items[index] ? items[index].getAttribute("data-media-role") || "" : "";
        progressButtons.forEach(function (button, buttonIndex) {
          button.setAttribute("aria-current", buttonIndex === index ? "true" : "false");
        });
        thumbs.forEach(function (button, buttonIndex) {
          button.setAttribute("aria-current", buttonIndex === index ? "true" : "false");
        });
        if (progressFill && items.length) {
          progressFill.style.width = (((index + 1) / items.length) * 100).toFixed(2) + "%";
        }
      }

      function scrollToItem(target, index) {
        if (!trackElement || !target) return;
        markInteracted();
        if (getComputedStyle(trackElement).overflowX === "visible") {
          target.scrollIntoView({ behavior: !Motion.enabled ? "auto" : "smooth", block: "nearest" });
        } else if (trackElement.scrollTo) {
          trackElement.scrollTo({ left: target.offsetLeft, behavior: !Motion.enabled ? "auto" : "smooth" });
        } else {
          target.scrollIntoView({ behavior: !Motion.enabled ? "auto" : "smooth", block: "nearest", inline: "start" });
        }
        setActive(index);
      }

      function renderInspection(index) {
        if (!inspectionLinks.length) return;
        inspectionIndex = Math.max(0, Math.min(inspectionLinks.length - 1, index));
        var source = inspectionLinks[inspectionIndex];
        inspectionPrevious.disabled = inspectionIndex === 0;
        inspectionNext.disabled = inspectionIndex === inspectionLinks.length - 1;
        inspectionCaption.textContent = source.getAttribute("data-media-label") || "Image " + (inspectionIndex + 1) + " of " + inspectionLinks.length;
        inspectionImage.alt = source.getAttribute("data-inspection-alt") || "Product inspection";
        inspectionImage.hidden = false;
        inspectionError.hidden = true;
        inspection.setAttribute("aria-busy", "true");
        inspectionImage.src = source.href;
      }

      function createInspection() {
        if (inspection) return;
        inspection = create("div", "gdk-overlay gdk-inspection");
        inspection.dataset.overlay = inspectionName;
        inspection.hidden = true;
        inspection.setAttribute("aria-hidden", "true");
        var backdrop = create("button", "gdk-overlay__backdrop");
        backdrop.type = "button";
        backdrop.dataset.overlayClose = "";
        backdrop.tabIndex = -1;
        backdrop.setAttribute("aria-label", "Close shirt inspection");
        var dialog = create("section", "gdk-inspection__dialog");
        dialog.setAttribute("role", "dialog");
        dialog.setAttribute("aria-modal", "true");
        dialog.setAttribute("aria-labelledby", inspectionName + "-title");
        dialog.tabIndex = -1;
        var header = create("header", "gdk-inspection__header");
        var title = create("h2", "", "Shirt inspection");
        title.id = inspectionName + "-title";
        var closeButton = create("button", "gdk-text-button", "Close");
        closeButton.type = "button";
        closeButton.dataset.overlayClose = "";
        closeButton.setAttribute("aria-label", "Close shirt inspection");
        header.append(title, closeButton);
        var figure = create("figure", "gdk-inspection__figure");
        inspectionImage = create("img", "gdk-inspection__image");
        inspectionImage.dataset.inspectionImage = "";
        inspectionImage.decoding = "async";
        inspectionImage.width = 1600;
        inspectionImage.height = 2000;
        inspectionError = create("p", "gdk-inspection__error", "This detailed image could not load. Close to return to the product gallery.");
        inspectionError.setAttribute("role", "status");
        inspectionError.hidden = true;
        figure.append(inspectionImage, inspectionError);
        var controls = create("div", "gdk-inspection__controls");
        inspectionPrevious = create("button", "gdk-text-button", "Previous");
        inspectionPrevious.type = "button";
        inspectionPrevious.dataset.inspectionPrev = "";
        inspectionNext = create("button", "gdk-text-button", "Next");
        inspectionNext.type = "button";
        inspectionNext.dataset.inspectionNext = "";
        inspectionCaption = create("span", "gdk-inspection__caption");
        inspectionCaption.dataset.inspectionCaption = "";
        inspectionCaption.setAttribute("aria-live", "polite");
        controls.append(inspectionPrevious, inspectionCaption, inspectionNext);
        dialog.append(header, figure, controls);
        inspection.append(backdrop, dialog);
        gallery.appendChild(inspection);
        life.on(inspectionPrevious, "click", function () { renderInspection(inspectionIndex - 1); });
        life.on(inspectionNext, "click", function () { renderInspection(inspectionIndex + 1); });
        life.on(inspection, "keydown", function (event) {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            renderInspection(inspectionIndex + (event.key === "ArrowRight" ? 1 : -1));
          }
        });
        var touchStart = null;
        life.on(figure, "pointerdown", function (event) {
          if (event.pointerType === "touch") touchStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        }, { passive: true });
        life.on(figure, "pointercancel", function () { touchStart = null; }, { passive: true });
        life.on(figure, "pointerup", function (event) {
          if (!touchStart || event.pointerId !== touchStart.id) return;
          var dx = event.clientX - touchStart.x;
          var dy = event.clientY - touchStart.y;
          touchStart = null;
          if (Math.abs(dx) >= 60 && Math.abs(dx) > Math.abs(dy) * 1.3) renderInspection(inspectionIndex + (dx < 0 ? 1 : -1));
        }, { passive: true });
        life.on(inspectionImage, "load", function () { inspection.removeAttribute("aria-busy"); });
        life.on(inspectionImage, "error", function () {
          inspection.removeAttribute("aria-busy");
          inspectionImage.hidden = true;
          inspectionError.hidden = false;
        });
        life.add(function () {
          OverlayController.close(inspection, false, true);
          inspection.remove();
        });
      }

      inspectionLinks.forEach(function (link, index) {
        life.on(link, "click", function (event) {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          createInspection();
          renderInspection(index);
          OverlayController.open(inspectionName, link);
        });
      });

      if (trackElement && items.length) {
        life.on(trackElement, "scroll", function () {
          if (ticking) return;
          ticking = true;
          life.frame(function () {
            ticking = false;
            var nearest = items.reduce(function (best, item, index) {
              var distance = Math.abs(item.offsetLeft - trackElement.scrollLeft);
              return distance < best.distance ? { index: index, distance: distance } : best;
            }, { index: 0, distance: Infinity });
            if (Math.abs(trackElement.scrollLeft) > 10 || nearest.index > 0) markInteracted();
            setActive(nearest.index);
          });
        }, { passive: true });
        life.on(trackElement, "pointerdown", function () {
          if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) markInteracted();
        }, { passive: true });
      }

      progressButtons.forEach(function (button, index) {
        life.on(button, "click", function () {
          scrollToItem(items[index], index);
        });
      });

      thumbs.forEach(function (thumb, index) {
        life.on(thumb, "click", function () {
          var id = thumb.getAttribute("data-media-id");
          var target = items.find(function (item) { return item.getAttribute("data-media-id") === id; });
          if (!target) return;
          scrollToItem(target, index);
        });
      });

      setActive(0);
    });
  }

  /* World Game navigation: swipe-first on mobile, focused club deck on desktop. */
  function initTabs(root) {
    componentElements("[data-gdk-tabs]", root).forEach(function (tabs) {
      var life = componentScope(tabs, "world");
      if (!life) return;

      var controls = all("[data-gdk-tab]", tabs);
      var panels = all("[data-gdk-tab-panel]", tabs);
      var rail = one("[data-gdk-world-rail]", tabs);
      var currentNode = one("[data-gdk-world-current]", tabs);
      var totalNode = one("[data-gdk-world-total]", tabs);
      var progressNode = one("[data-gdk-world-progress]", tabs);
      var prevButton = one("[data-gdk-world-prev]", tabs);
      var nextButton = one("[data-gdk-world-next]", tabs);
      var swipeCue = one("[data-gdk-world-swipe-cue]", tabs);
      var mobileQuery = window.matchMedia("(max-width: 767px)");
      var desktopDeckQuery = window.matchMedia("(min-width: 990px)");
      var currentIndex = Math.max(0, controls.findIndex(function (control) { return control.checked; }));
      var scrollFrame = 0;
      var resizeFrame = 0;
      var wheelAccumulator = 0;
      var wheelResetTimer = 0;
      var dragStartX = 0;
      var dragPointerId = null;
      var dragPanel = null;
      var dragging = false;
      var suppressClick = false;
      var suppressMobileScrollInteraction = false;
      var suppressMobileScrollTimer = 0;

      if (!controls.length || !panels.length || !rail) return;
      currentIndex = Math.min(currentIndex, Math.min(controls.length, panels.length) - 1);

      function padPosition(value) {
        return String(value).padStart(2, "0");
      }

      function activeLabelFor(index) {
        var control = controls[index];
        return control ? control.nextElementSibling : null;
      }

      function updateAccent(index) {
        var label = activeLabelFor(index);
        if (!label) return;
        var accent = label.style.getPropertyValue("--gdk-club-accent") || window.getComputedStyle(label).getPropertyValue("--gdk-club-accent");
        if (accent) tabs.style.setProperty("--gdk-club-accent", accent.trim());
      }

      function updatePosition(index) {
        if (currentNode) currentNode.textContent = padPosition(index + 1);
        if (totalNode) totalNode.textContent = padPosition(panels.length);
        if (progressNode) progressNode.style.width = (((index + 1) / panels.length) * 100).toFixed(2) + "%";
        if (prevButton) prevButton.disabled = index <= 0;
        if (nextButton) nextButton.disabled = index >= panels.length - 1;
      }

      function centerActiveLabel(index, smooth) {
        var activeLabel = activeLabelFor(index);
        if (!activeLabel) return;
        var tabRail = activeLabel.closest(".gdk-international__tablist");
        if (!tabRail || tabRail.scrollWidth <= tabRail.clientWidth) return;

        /* Never use Element.scrollIntoView here. On mobile Safari that can
           move the document vertically to World Game during initialization.
           Scroll only the horizontal selector rail. */
        var desiredLeft = activeLabel.offsetLeft - ((tabRail.clientWidth - activeLabel.offsetWidth) / 2);
        var maxLeft = Math.max(0, tabRail.scrollWidth - tabRail.clientWidth);
        desiredLeft = Math.max(0, Math.min(desiredLeft, maxLeft));
        tabRail.scrollTo({
          left: desiredLeft,
          behavior: smooth && Motion.enabled ? "smooth" : "auto"
        });
      }

      function markActivePanel(index) {
        panels.forEach(function (panel, panelIndex) {
          panel.classList.toggle("is-world-active", panelIndex === index);
          panel.classList.toggle("is-world-neighbor", Math.abs(panelIndex - index) === 1);
        });
      }

      function setDesktopPanel(index, direction) {
        panels.forEach(function (panel, panelIndex) {
          var active = panelIndex === index;
          panel.hidden = !active;
          panel.classList.toggle("is-world-active", active);
          panel.classList.remove("is-world-entering-next", "is-world-entering-prev", "is-world-neighbor");
          panel.style.transform = "";
          if (active && direction && Motion.enabled) {
            panel.classList.add(direction === "prev" ? "is-world-entering-prev" : "is-world-entering-next");
            life.timeout(function () {
              panel.classList.remove("is-world-entering-next", "is-world-entering-prev");
            }, 380);
          }
        });
      }

      function unhideMobilePanels(index) {
        panels.forEach(function (panel) {
          panel.hidden = false;
          panel.classList.remove("is-world-entering-next", "is-world-entering-prev");
          panel.style.transform = "";
        });
        markActivePanel(index);
      }

      function scrollPanelIntoView(index, smooth, suppressInteraction) {
        var panel = panels[index];
        if (!panel) return;
        var left = panel.offsetLeft;
        if (suppressInteraction) {
          suppressMobileScrollInteraction = true;
          life.clearTimeout(suppressMobileScrollTimer);
          suppressMobileScrollTimer = life.timeout(function () {
            suppressMobileScrollInteraction = false;
          }, smooth && Motion.enabled ? 420 : 90);
        }
        rail.scrollTo({ left: left, behavior: smooth && Motion.enabled ? "smooth" : "auto" });
      }

      function syncControls(index) {
        controls.forEach(function (control, controlIndex) {
          control.checked = controlIndex === index;
        });
      }

      function activateIndex(index, options) {
        options = options || {};
        var nextIndex = Math.max(0, Math.min(index, panels.length - 1));
        var previousIndex = currentIndex;
        var direction = nextIndex < previousIndex ? "prev" : (nextIndex > previousIndex ? "next" : "");
        currentIndex = nextIndex;
        syncControls(currentIndex);
        updateAccent(currentIndex);
        updatePosition(currentIndex);
        centerActiveLabel(currentIndex, options.smooth !== false);

        if (mobileQuery.matches) {
          unhideMobilePanels(currentIndex);
          if (options.moveRail !== false) scrollPanelIntoView(currentIndex, options.smooth !== false);
        } else {
          setDesktopPanel(currentIndex, direction);
          rail.scrollLeft = 0;
        }
      }

      function markWorldInteracted() {
        if (tabs.classList.contains("has-world-interacted")) return;
        tabs.classList.add("has-world-interacted");
        if (swipeCue) swipeCue.setAttribute("aria-hidden", "true");
      }

      function nearestMobilePanel() {
        var viewportCenter = rail.scrollLeft + (rail.clientWidth / 2);
        var nearestIndex = 0;
        var nearestDistance = Infinity;
        panels.forEach(function (panel, panelIndex) {
          var panelCenter = panel.offsetLeft + (panel.offsetWidth / 2);
          var distance = Math.abs(panelCenter - viewportCenter);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = panelIndex;
          }
        });
        return nearestIndex;
      }

      function configureResponsiveMode() {
        if (mobileQuery.matches) {
          unhideMobilePanels(currentIndex);
          life.frame(function () {
            /* Initial responsive setup must never count as user interaction
               or affect the document scroll position. */
            scrollPanelIntoView(currentIndex, false, true);
          });
        } else {
          setDesktopPanel(currentIndex, "");
        }
        updatePosition(currentIndex);
        updateAccent(currentIndex);
      }

      controls.forEach(function (control, index) {
        life.on(control, "change", function () {
          if (control.checked) {
            markWorldInteracted();
            activateIndex(index, { moveRail: true, smooth: true });
          }
        });
      });

      if (prevButton) {
        life.on(prevButton, "click", function () {
          if (prevButton.disabled) return;
          markWorldInteracted();
          activateIndex(currentIndex - 1, { moveRail: true, smooth: true });
        });
      }
      if (nextButton) {
        life.on(nextButton, "click", function () {
          if (nextButton.disabled) return;
          markWorldInteracted();
          activateIndex(currentIndex + 1, { moveRail: true, smooth: true });
        });
      }

      life.on(rail, "scroll", function () {
        if (!mobileQuery.matches) return;
        if (!suppressMobileScrollInteraction) markWorldInteracted();
        if (scrollFrame) return;
        scrollFrame = life.frame(function () {
          scrollFrame = 0;
          var nearestIndex = nearestMobilePanel();
          if (nearestIndex !== currentIndex) activateIndex(nearestIndex, { moveRail: false, smooth: false });
        });
      }, { passive: true });

      life.on(rail, "keydown", function (event) {
        if (event.target && event.target.closest("a,button,input,select,textarea")) return;
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        markWorldInteracted();
        activateIndex(currentIndex + (event.key === "ArrowRight" ? 1 : -1), { moveRail: true, smooth: true });
      });

      life.on(rail, "wheel", function (event) {
        if (!desktopDeckQuery.matches) return;
        if (Math.abs(event.deltaX) < 10 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        wheelAccumulator += event.deltaX;
        life.clearTimeout(wheelResetTimer);
        wheelResetTimer = life.timeout(function () { wheelAccumulator = 0; }, 220);
        if (Math.abs(wheelAccumulator) < 38) return;
        var direction = wheelAccumulator > 0 ? 1 : -1;
        wheelAccumulator = 0;
        markWorldInteracted();
        activateIndex(currentIndex + direction, { moveRail: false, smooth: true });
      }, { passive: false });

      life.on(rail, "pointerdown", function (event) {
        if (!desktopDeckQuery.matches || event.pointerType !== "mouse" || event.button !== 0) return;
        if (event.target.closest("a,button,input,label")) return;
        dragStartX = event.clientX;
        dragPanel = panels[currentIndex];
        dragging = false;
        if (rail.setPointerCapture) {
          dragPointerId = event.pointerId;
          rail.setPointerCapture(dragPointerId);
        }
      });

      life.on(rail, "pointermove", function (event) {
        if (!dragPanel || !desktopDeckQuery.matches) return;
        var delta = event.clientX - dragStartX;
        if (Math.abs(delta) < 6 && !dragging) return;
        dragging = true;
        event.preventDefault();
        rail.classList.add("is-world-dragging");
        var damped = Math.max(-34, Math.min(34, delta * .3));
        dragPanel.style.transform = Motion.enabled ? "translate3d(" + damped + "px,0,0)" : "";
      });

      function finishDesktopDrag(event) {
        if (!dragPanel) return;
        var endX = typeof event.clientX === "number" ? event.clientX : dragStartX;
        var delta = endX - dragStartX;
        var wasDragging = dragging;
        dragPanel.style.transform = "";
        dragPanel = null;
        dragging = false;
        rail.classList.remove("is-world-dragging");
        if (!wasDragging) return;
        suppressClick = true;
        markWorldInteracted();
        if (Math.abs(delta) >= 58) activateIndex(currentIndex + (delta < 0 ? 1 : -1), { moveRail: false, smooth: true });
      }

      life.on(rail, "pointerup", finishDesktopDrag);
      life.on(rail, "pointercancel", finishDesktopDrag);
      life.on(rail, "click", function (event) {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, true);

      function onModeChange() {
        if (resizeFrame) life.cancelFrame(resizeFrame);
        resizeFrame = life.frame(function () {
          resizeFrame = 0;
          configureResponsiveMode();
        });
      }
      life.media(mobileQuery, onModeChange);
      var clearDrag = function () {
        if (dragPointerId !== null && rail.hasPointerCapture && rail.hasPointerCapture(dragPointerId)) rail.releasePointerCapture(dragPointerId);
        dragPointerId = null;
        panels.forEach(function (panel) { panel.style.transform = ""; });
        rail.classList.remove("is-world-dragging");
        dragPanel = null;
        dragging = false;
        suppressClick = false;
      };
      life.on(document, "gdk:motion-change", function () { if (!Motion.enabled) clearDrag(); });
      life.add(function () {
        clearDrag();
        panels.forEach(function (panel) { panel.classList.remove("is-world-entering-next", "is-world-entering-prev"); });
        delete tabs._gdkSelectBlock;
        delete tabs._gdkSelectClub;
      });
      tabs._gdkSelectBlock = function (target) {
        var index = panels.findIndex(function (panel) { return panel === target || panel.contains(target); });
        if (index >= 0) activateIndex(index, { moveRail: true, smooth: false });
      };
      tabs._gdkSelectClub = function (key) {
        var index = controls.findIndex(function (control) { return control.getAttribute("data-club-key") === key; });
        if (index >= 0) activateIndex(index, { moveRail: true, smooth: false });
      };

      activateIndex(currentIndex, { moveRail: false, smooth: false });
      configureResponsiveMode();
    });
  }

  /* Home / Away commerce switch inside the selected World Game club. */
  function initWorldProductStages(root) {
    componentElements("[data-gdk-world-products]", root).forEach(function (productStage) {
      var life = componentScope(productStage, "world-products");
      if (!life) return;
      var panel = productStage.closest("[data-gdk-tab-panel]") || productStage.parentElement;
      var controls = all("[data-gdk-world-product-tab]", panel);
      var stages = all("[data-gdk-world-product-stage]", productStage);
      var transitionTimer = 0;
      if (!stages.length) return;

      function activate(key, options) {
        options = options || {};
        var target = stages.find(function (stage) { return stage.getAttribute("data-product-key") === key; }) || stages[0];
        var resolvedKey = target.getAttribute("data-product-key");
        var previous = stages.find(function (stage) { return !stage.hidden && stage.classList.contains("is-active"); });
        productStage.setAttribute("data-active-product-key", resolvedKey || "");
        controls.forEach(function (control) {
          var active = control.getAttribute("data-product-key") === resolvedKey;
          control.classList.toggle("is-active", active);
          control.setAttribute("aria-selected", active ? "true" : "false");
          control.tabIndex = active ? 0 : -1;
          if (active) control.setAttribute("aria-current", "true"); else control.removeAttribute("aria-current");
        });
        stages.forEach(function (stage) {
          var active = stage === target;
          stage.hidden = !active;
          stage.setAttribute("aria-hidden", active ? "false" : "true");
          stage.classList.toggle("is-active", active);
          stage.classList.remove("is-product-entering");
        });
        life.clearTimeout(transitionTimer);
        if (previous && previous !== target && Motion.enabled && options.animate !== false) {
          target.classList.add("is-product-entering");
          transitionTimer = life.timeout(function () { target.classList.remove("is-product-entering"); }, 280);
        }
      }

      controls.forEach(function (control, index) {
        life.on(control, "click", function (event) {
          event.preventDefault();
          activate(control.getAttribute("data-product-key"));
        });
        life.on(control, "keydown", function (event) {
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight" && event.key !== "Home" && event.key !== "End") return;
          event.preventDefault();
          var nextIndex = event.key === "Home" ? 0 : (event.key === "End" ? controls.length - 1 : index + (event.key === "ArrowRight" ? 1 : -1));
          nextIndex = Math.max(0, Math.min(nextIndex, controls.length - 1));
          controls[nextIndex].focus({ preventScroll: true });
          activate(controls[nextIndex].getAttribute("data-product-key"));
        });
      });
      life.add(function () {
        stages.forEach(function (stage) { stage.classList.remove("is-product-entering"); });
        productStage.removeAttribute("data-active-product-key");
      });
      activate(productStage.getAttribute("data-default-product-key") || stages[0].getAttribute("data-product-key"), { animate: false });
    });
  }

  /* Local / International switch inside My Club discovery. */
  function activateClubGroup(section, group, options) {
    if (!section || (group !== "local" && group !== "international")) return;
    options = options || {};
    section.setAttribute("data-active-group", group);
    all("[data-gdk-club-group-tab]", section).forEach(function (button) {
      var active = button.getAttribute("data-gdk-club-group-tab") === group;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
    all("[data-club-item]", section).forEach(function (item) {
      item.hidden = item.getAttribute("data-club-group") !== group;
    });
    if (options.focusRail) {
      var first = one("[data-club-item]:not([hidden]) [data-gdk-club-choice]", section);
      if (first) first.focus({ preventScroll: true });
    }
  }

  function initClubGroups(root) {
    componentElements("[data-gdk-club-groups]", root || document).forEach(function (section) {
      var life = componentScope(section, "club-groups");
      if (!life) return;
      var initial = section.getAttribute("data-active-group") || "local";
      activateClubGroup(section, initial);
      all("[data-gdk-club-group-tab]", section).forEach(function (button) {
        life.on(button, "click", function () {
          activateClubGroup(section, button.getAttribute("data-gdk-club-group-tab"));
          track("club_group_viewed", { group: button.getAttribute("data-gdk-club-group-tab") });
        });
      });
      var change = one("[data-gdk-change-club]", section);
      if (change) {
        life.on(change, "click", function () {
          var preference = readMyClub();
          section.classList.add("is-changing-club");
          if (preference && preference.group) activateClubGroup(section, preference.group);
          var current = preference && all("[data-gdk-club-choice]", section).find(function (choice) {
            return choice.getAttribute("data-club-key") === preference.key;
          });
          var target = current || one("[data-club-item]:not([hidden]) [data-gdk-club-choice]", section);
          var instruction = one("[data-gdk-club-selection-status]", section);
          if (instruction) instruction.textContent = "Choose another badge to replace " + (preference ? preference.name : "your club") + ".";
          if (target) target.focus({ preventScroll: true });
        });
      }
      var clear = one("[data-gdk-clear-club]", section);
      if (clear) {
        life.on(clear, "click", function () {
          clearMyClub();
          section.classList.remove("is-changing-club");
          applyMyClub(null);
          activateClubGroup(section, section.getAttribute("data-default-group") || "local");
          var first = one("[data-club-item]:not([hidden]) [data-gdk-club-choice]", section);
          if (first) first.focus({ preventScroll: true });
          track("club_cleared", {});
        });
      }
    });

    componentElements("[data-gdk-final-group]", root || document).forEach(function (link) {
      var life = componentScope(link, "club-link");
      if (!life) return;
      life.on(link, "click", function () {
        var section = one("[data-gdk-club-groups]");
        if (section) activateClubGroup(section, link.getAttribute("data-gdk-final-group"));
      });
    });
  }

  /* My Club: session-level supporter preference that subtly connects homepage discovery. */
  var MY_CLUB_STORAGE_KEY = "gdk_my_club";
  var MY_CLUB_STORAGE_VERSION = 1;

  function safeClubHref(value) {
    if (!value || String(value).charAt(0) === "#") return "";
    try {
      var url = new URL(String(value), window.location.origin);
      if (url.origin !== window.location.origin || !/^\/(collections|products)\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return "";
      return url.pathname;
    } catch (error) {
      return "";
    }
  }

  function normaliseMyClub(preference) {
    if (!preference || typeof preference !== "object") return null;
    var key = normaliseClubKey(preference.key);
    var name = typeof preference.name === "string" ? preference.name.trim() : "";
    var code = typeof preference.code === "string" ? preference.code.trim().slice(0, 12) : "";
    var group = preference.group === "local" || preference.group === "international" ? preference.group : "";
    if (!key || !name || name.length > 80) return null;
    return {
      version: MY_CLUB_STORAGE_VERSION,
      key: key,
      name: name,
      code: code,
      accent: /^#[0-9a-f]{3,8}$/i.test(preference.accent || "") ? preference.accent : "#0B0D0C",
      group: group,
      href: safeClubHref(preference.href),
      storedAt: Number(preference.storedAt) || Date.now()
    };
  }

  function readMyClub() {
    try {
      var stored = window.sessionStorage.getItem(MY_CLUB_STORAGE_KEY);
      if (!stored) return null;
      var parsed = normaliseMyClub(JSON.parse(stored));
      if (!parsed) window.sessionStorage.removeItem(MY_CLUB_STORAGE_KEY);
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function storeMyClub(preference) {
    var normalised = normaliseMyClub(preference);
    if (!normalised) return null;
    try {
      window.sessionStorage.setItem(MY_CLUB_STORAGE_KEY, JSON.stringify(normalised));
    } catch (error) {}
    return normalised;
  }

  function clearMyClub() {
    try { window.sessionStorage.removeItem(MY_CLUB_STORAGE_KEY); } catch (error) {}
  }

  function preferenceFromClubElement(element) {
    if (!element) return null;
    return normaliseMyClub({
      key: element.getAttribute("data-club-key") || "",
      name: element.getAttribute("data-club-name") || "",
      code: element.getAttribute("data-club-code") || "",
      accent: element.getAttribute("data-club-accent") || "#0B0D0C",
      group: element.getAttribute("data-club-group") || "",
      href: element.getAttribute("data-club-href") || element.getAttribute("href") || "",
      storedAt: Date.now()
    });
  }

  function recogniseClubPriorityContainers(preference) {
    var activeKey = preference && preference.key;
    all("[data-gdk-kit-room], [data-gdk-club-priority]").forEach(function (container) {
      var surfaces = Array.prototype.slice.call(container.children).filter(function (child) {
        return child.matches && child.matches("[data-gdk-club-surface]");
      });
      var recognised = surfaces.some(function (surface) { return Boolean(activeKey && surface.getAttribute("data-club-key") === activeKey); });
      container.classList.toggle("has-my-club", recognised);
      surfaces.forEach(function (surface) { delete surface.dataset.gdkOriginalOrder; });
    });
  }

  function applyMyClub(preference, options) {
    options = options || {};
    var hasPreference = Boolean(preference && preference.key);
    if (hasPreference && (!preference.group || !preference.href || preference.href === "#gdk-club-selector")) {
      var matchingChoice = all("[data-gdk-club-choice]").find(function (choice) {
        return choice.getAttribute("data-club-key") === preference.key;
      });
      if (matchingChoice) {
        preference.group = preference.group || matchingChoice.getAttribute("data-club-group") || "";
        preference.href = matchingChoice.getAttribute("data-club-href") || preference.href || "#gdk-club-selector";
      }
    }
    if (hasPreference) document.documentElement.setAttribute("data-gdk-my-club", preference.key);
    else document.documentElement.removeAttribute("data-gdk-my-club");

    all("[data-gdk-club-choice]").forEach(function (choice) {
      var active = hasPreference && choice.getAttribute("data-club-key") === preference.key;
      choice.classList.toggle("is-my-club", active);
      choice.setAttribute("aria-pressed", active ? "true" : "false");
      all("[data-my-club-state]", choice).forEach(function (state) { state.hidden = !active; });
      var actionText = one(".gdk-club-card__action span", choice);
      if (actionText) actionText.textContent = active ? "SELECTED" : "CHOOSE";
    });

    all("[data-gdk-club-surface]").forEach(function (surface) {
      var active = hasPreference && surface.getAttribute("data-club-key") === preference.key;
      surface.classList.toggle("is-my-club", active);
      surface.style.removeProperty("order");
      all("[data-my-club-state]", surface).forEach(function (state) { state.hidden = !active; });
    });
    recogniseClubPriorityContainers(hasPreference ? preference : null);

    all("[data-product-card][data-club-key]").forEach(function (card) {
      var active = hasPreference && card.getAttribute("data-club-key") === preference.key;
      card.classList.toggle("is-my-club", active);
      card.style.removeProperty("order");
      all("[data-my-club-state]", card).forEach(function (state) { state.hidden = !active; });
    });

    var clubSection = one("[data-gdk-club-groups]");
    if (hasPreference && clubSection && preference.group) {
      activateClubGroup(clubSection, preference.group);
    }

    if (hasPreference && !options.skipWorldStage) {
      var matchingControl = all("[data-gdk-tab][data-club-key]").find(function (control) {
        return control.getAttribute("data-club-key") === preference.key;
      });
      var tabs = matchingControl && matchingControl.closest("[data-gdk-tabs]");
      if (tabs && typeof tabs._gdkSelectClub === "function") {
        tabs._gdkSelectClub(preference.key);
      }
    }

    all("[data-gdk-personalisation-studio]").forEach(function (studio) {
      // The upgraded studio manages its own selected club and listens for the My Club event below.
      if (one("[data-home-club-selector]", studio)) return;
      var clubLabel = hasPreference ? "YOUR CLUB / " + preference.name.toUpperCase() : "";
      all("[data-personalisation-club]", studio).forEach(function (target) { target.textContent = clubLabel; });
      all("[data-personalisation-club-context]", studio).forEach(function (target) { target.hidden = !hasPreference; });
      all("[data-personalisation-club-media]", studio).forEach(function (target) { target.textContent = hasPreference ? preference.name : ""; });
      studio.style.setProperty("--gdk-club-accent", hasPreference ? preference.accent : "#D4AF37");
    });

    var hero = one("[data-gdk-hero]");
    if (hero) hero.style.setProperty("--gdk-club-accent", hasPreference ? preference.accent : "#0B0D0C");
    var heroState = one("[data-hero-club-state]");
    var heroName = one("[data-hero-club-name]");
    if (heroState) heroState.hidden = !hasPreference;
    if (heroName) heroName.textContent = hasPreference ? preference.name : "";

    var headerMyClub = one("[data-header-my-club]");
    var headerMyClubCode = one("[data-header-my-club-code]");
    if (headerMyClub) {
      headerMyClub.hidden = !hasPreference;
      headerMyClub.href = config.clubSelectorUrl || "/#gdk-club-selector";
      headerMyClub.style.setProperty("--gdk-club-accent", hasPreference ? preference.accent : "#0B0D0C");
      headerMyClub.setAttribute("aria-label", hasPreference ? "My club: " + preference.name + ". Change club." : "Choose my club");
    }
    if (headerMyClubCode) headerMyClubCode.textContent = hasPreference ? (preference.code || preference.name) : "";

    var mobileMyClub = one("[data-mobile-my-club]");
    var mobileMyClubName = one("[data-mobile-my-club-name]");
    if (mobileMyClub) {
      mobileMyClub.hidden = !hasPreference;
      mobileMyClub.href = config.clubSelectorUrl || "/#gdk-club-selector";
      mobileMyClub.style.setProperty("--gdk-club-accent", hasPreference ? preference.accent : "#0B0D0C");
    }
    if (mobileMyClubName) mobileMyClubName.textContent = hasPreference ? preference.name : "";

    var selection = one("[data-gdk-club-selection]");
    var selectionName = one("[data-gdk-club-selection-name]");
    var selectionLink = one("[data-gdk-club-selection-link]");
    var selectionLinkLabel = one("[data-gdk-club-selection-link-label]");
    var selectionStatus = one("[data-gdk-club-selection-status]");
    var clubSectionAccent = one("[data-gdk-club-groups]");
    var selectionChange = one("[data-gdk-change-club]");
    var selectionClear = one("[data-gdk-clear-club]");
    var selectionEmpty = one("[data-gdk-club-selection-empty]");
    if (selection) selection.classList.toggle("has-selection", hasPreference);
    if (selectionName) selectionName.textContent = hasPreference ? preference.name : "NOT SET";
    if (selectionLink) {
      selectionLink.hidden = !hasPreference || !preference.href;
      if (hasPreference && preference.href) selectionLink.href = preference.href;
    }
    if (selectionLinkLabel && hasPreference) selectionLinkLabel.textContent = "SHOP " + preference.name.toUpperCase();
    if (selectionStatus) selectionStatus.textContent = hasPreference ? preference.name + " is saved as My Club. Shopping another club will not change it." : "Choose a badge to save your club. Shopping a club is a separate action.";
    if (selectionChange) selectionChange.hidden = !hasPreference;
    if (selectionClear) selectionClear.hidden = !hasPreference;
    if (selectionEmpty) selectionEmpty.hidden = hasPreference;
    if (clubSectionAccent) clubSectionAccent.style.setProperty("--gdk-club-accent", hasPreference ? preference.accent : "#0B0D0C");

    var finalState = one("[data-final-club-state]");
    var finalName = one("[data-final-club-name]");
    var finalLink = one("[data-final-club-link]");
    var finalLabel = one("[data-final-club-link-label]");
    var finalHeading = one("[data-final-heading]");
    var finalCopy = one("[data-final-copy]");
    var finalGroups = one("[data-final-group-links]");
    if (finalState) finalState.hidden = !hasPreference;
    if (finalName) finalName.textContent = hasPreference ? preference.name : "";
    if (finalHeading) finalHeading.textContent = hasPreference ? "YOUR SIDE IS SET." : (finalHeading.getAttribute("data-default-heading") || "FIND YOUR SIDE.");
    if (finalCopy) finalCopy.textContent = hasPreference ? preference.name + " is ready for matchday. Go straight to your club or keep exploring the full edit." : (finalCopy.getAttribute("data-default-copy") || "Local or international — get straight to the shirts that matter to you.");
    if (finalGroups) finalGroups.hidden = hasPreference;
    if (finalLink && finalLabel) {
      if (hasPreference) {
        finalLink.href = preference.href || "#gdk-club-selector";
        finalLabel.textContent = preference.href ? "SHOP " + preference.name.toUpperCase() : "CHANGE YOUR CLUB";
      } else {
        finalLink.href = "#gdk-club-selector";
        finalLabel.textContent = finalLink.getAttribute("data-default-label") || "CHOOSE YOUR CLUB";
      }
    }

    document.dispatchEvent(new CustomEvent("gdk:my-club", { detail: hasPreference ? preference : null }));
  }

  function initMyClub(root) {
    var scope = root || document;
    componentElements("[data-gdk-club-choice]", scope).forEach(function (choice) {
      var life = componentScope(choice, "my-club");
      if (!life) return;
      life.on(choice, "click", function () {
        var preference = preferenceFromClubElement(choice);
        if (!preference || !preference.key) return;
        preference = storeMyClub(preference);
        if (!preference) return;
        var section = choice.closest("[data-gdk-club-groups]");
        if (section) section.classList.remove("is-changing-club");
        applyMyClub(preference);
        track("club_selected", { club: preference.name, key: preference.key, group: preference.group });
      });
    });
    applyMyClub(readMyClub());
  }

  /* Homepage personalisation studio: club-aware live preview; final configuration remains on PDP. */
  function initHomepagePersonalisation(root) {
    componentElements("[data-gdk-personalisation-studio]", root || document).forEach(function (studio) {
      var life = componentScope(studio, "studio");
      if (!life) return;
      var nameInput = one("[data-home-personalisation-name]", studio);
      var numberInput = one("[data-home-personalisation-number]", studio);
      var previewNames = all("[data-home-preview-name]", studio);
      var previewNumbers = all("[data-home-preview-number]", studio);
      var previewImage = one("[data-home-preview-image]", studio);
      var media = one("[data-home-preview-media]", studio);
      var clubChoices = all("[data-home-club-choice]", studio);
      var groupTabs = all("[data-home-club-group-tab]", studio);
      var groupPanels = all("[data-home-club-group]", studio);
      var cta = one("[data-home-personalisation-cta]", studio);
      var ctaLabel = one("[data-home-personalisation-cta-label]", studio);
      var selectedDraftChoice = null;
      var draftEdited = false;
      var switchSerial = 0;
      var removeImageLoad = function () {};
      var imageSwitchTimer;
      life.add(function () {
        studio.classList.remove("is-switching", "is-editing");
        if (media) media.removeAttribute("aria-busy");
      });

      function renderPreview() {
        if (!nameInput || !numberInput) return;
        var value = personalisationValue(nameInput, numberInput, false);
        previewNames.forEach(function (target) { target.textContent = value.name || "YOUR NAME"; });
        previewNumbers.forEach(function (target) { target.textContent = value.number || "10"; });
      }

      function activateGroup(group) {
        if (!group) return;
        groupTabs.forEach(function (tab) {
          tab.setAttribute("aria-selected", tab.getAttribute("data-home-club-group-tab") === group ? "true" : "false");
        });
        groupPanels.forEach(function (panel) {
          panel.classList.toggle("is-active", panel.getAttribute("data-home-club-group") === group);
        });
      }

      function finishImageSwitch(serial) {
        if (serial !== switchSerial) return;
        studio.classList.remove("is-switching");
        if (media) media.removeAttribute("aria-busy");
      }

      function swapPreviewImage(choice) {
        if (!previewImage || !choice) return;
        var src = choice.getAttribute("data-preview-src") || "";
        var srcset = choice.getAttribute("data-preview-srcset") || "";
        var alt = choice.getAttribute("data-preview-alt") || "Shirt back personalisation preview";
        if (!src) return;
        var serial = ++switchSerial;
        studio.classList.add("is-switching");
        if (media) media.setAttribute("aria-busy", "true");
        var onLoad = function () { finishImageSwitch(serial); };
        removeImageLoad();
        life.clearTimeout(imageSwitchTimer);
        removeImageLoad = life.on(previewImage, "load", onLoad, { once: true });
        previewImage.alt = alt;
        if (srcset) previewImage.srcset = srcset;
        else previewImage.removeAttribute("srcset");
        previewImage.src = src;
        imageSwitchTimer = life.timeout(function () { removeImageLoad(); finishImageSwitch(serial); }, 800);
      }

      function selectClub(choice, options) {
        if (!choice) return;
        selectedDraftChoice = choice;
        var clubName = choice.getAttribute("data-club-name") || "YOUR CLUB";
        var clubCode = choice.getAttribute("data-club-code") || clubName;
        var group = choice.getAttribute("data-club-group") || "local";
        var accent = choice.getAttribute("data-club-accent") || "#D4AF37";
        var link = choice.getAttribute("data-preview-link") || "";
        var label = choice.getAttribute("data-preview-cta-label") || "PERSONALISE THIS SHIRT";

        clubChoices.forEach(function (button) {
          var active = button === choice;
          button.classList.toggle("is-active", active);
          button.setAttribute("aria-pressed", active ? "true" : "false");
        });
        activateGroup(group);
        studio.dataset.previewClub = clubName;
        studio.dataset.previewClubCode = clubCode;
        studio.style.setProperty("--gdk-club-accent", accent);
        applyPersonalisationStyle(studio, choice);
        all("[data-personalisation-club]", studio).forEach(function (target) { target.textContent = clubName; });
        all("[data-personalisation-club-media]", studio).forEach(function (target) { target.textContent = clubName; });

        if (cta) {
          if (link) {
            cta.href = link;
            cta.classList.remove("is-disabled");
            cta.removeAttribute("aria-disabled");
            cta.removeAttribute("tabindex");
          } else {
            cta.href = "#";
            cta.classList.add("is-disabled");
            cta.setAttribute("aria-disabled", "true");
            cta.tabIndex = -1;
          }
        }
        if (ctaLabel) ctaLabel.textContent = label;
        swapPreviewImage(choice);
        if (!(options && options.silent)) {
          track("personalisation_preview_club", { club: clubName, code: clubCode, group: group });
        }
      }

      if (nameInput && numberInput && previewNames.length && previewNumbers.length) {
        bindPersonalisationInputs(life, nameInput, numberInput, one("[data-home-personalisation-feedback]", studio), renderPreview);
        [nameInput, numberInput].forEach(function (input) {
          life.on(input, "input", function () { draftEdited = true; });
          life.on(input, "focus", function () { studio.classList.add("is-editing"); });
          life.on(input, "blur", function () {
            life.timeout(function () {
              if (!studio.contains(document.activeElement)) studio.classList.remove("is-editing");
            }, 0);
          });
        });
        renderPreview();
      }

      groupTabs.forEach(function (tab) {
        life.on(tab, "click", function () {
          activateGroup(tab.getAttribute("data-home-club-group-tab"));
        });
      });

      clubChoices.forEach(function (choice) {
        life.on(choice, "click", function () { selectClub(choice); });
      });

      var initialChoice = clubChoices.find(function (choice) { return choice.getAttribute("aria-pressed") === "true"; }) || clubChoices[0];
      var preference = readMyClub();
      if (preference) {
        var preferenceKey = normaliseClubKey(preference.key || preference.name);
        var preferenceChoice = clubChoices.find(function (choice) {
          return normaliseClubKey(choice.getAttribute("data-club-key") || choice.getAttribute("data-club-name")) === preferenceKey ||
            normaliseClubKey(choice.getAttribute("data-club-name")) === normaliseClubKey(preference.name);
        });
        if (preferenceChoice) initialChoice = preferenceChoice;
      }
      if (initialChoice) selectClub(initialChoice, { silent: true });

      life.on(document, "gdk:my-club", function (event) {
        if (!studio.isConnected || !event.detail) return;
        var nextKey = normaliseClubKey(event.detail.key || event.detail.name);
        var matchingChoice = clubChoices.find(function (choice) {
          return normaliseClubKey(choice.getAttribute("data-club-key") || choice.getAttribute("data-club-name")) === nextKey ||
            normaliseClubKey(choice.getAttribute("data-club-name")) === normaliseClubKey(event.detail.name);
        });
        if (matchingChoice) selectClub(matchingChoice, { silent: true });
      });

      if (cta) {
        life.on(cta, "click", function (event) {
          if (cta.getAttribute("aria-disabled") === "true") { event.preventDefault(); return; }
          clearPersonalisationDraft();
          if (!draftEdited || !nameInput || !numberInput ||
              nameInput.dataset.gdkComposing === "true" || numberInput.dataset.gdkComposing === "true") return;
          var choice = selectedDraftChoice || cta;
          var productId = choice.getAttribute("data-draft-product-id") || "";
          var productUrl = draftProductPath(choice.getAttribute("data-draft-product-url") || "");
          if (!/^\d+$/.test(productId) || !productUrl || draftProductPath(cta.href) !== productUrl) return;
          if (!personalisationValue(nameInput, numberInput, true).valid) return;
          try {
            window.sessionStorage.setItem(personalisationDraftKey, JSON.stringify({
              version: 1,
              productId: productId,
              productHandle: choice.getAttribute("data-draft-product-handle") || productUrl.split("/").pop(),
              url: productUrl,
              name: nameInput.value,
              number: numberInput.value,
              createdAt: Date.now()
            }));
          } catch (error) { /* Storage is optional; the genuine product link still works. */ }
        });
      }
    });
  }

  /* Matchday Flow: sparse below-fold reveals that never replace native scrolling. */
  function initHomepageFlow(root) {
    componentElements("[data-gdk-flow]", root || document).forEach(function (section) {
      var life = componentScope(section, "matchday-flow");
      if (!life) return;
      var items = all("[data-gdk-reveal]", section).slice(0, 3);
      var observer = null;
      var revealed = false;
      if (!items.length) return;

      function reveal(immediate) {
        if (revealed) return;
        revealed = true;
        if (observer) observer.disconnect();
        section.removeAttribute("data-gdk-reveal-ready");
        items.forEach(function (item, index) {
          var show = function () {
            item.classList.remove("is-gdk-reveal-pending");
            item.classList.add("is-gdk-revealed");
          };
          if (immediate || !Motion.enabled || index === 0) show();
          else life.timeout(show, Math.min(index * 36, 72));
        });
      }

      life.add(function () {
        if (observer) observer.disconnect();
        section.removeAttribute("data-gdk-reveal-ready");
        items.forEach(function (item) { item.classList.remove("is-gdk-reveal-pending", "is-gdk-revealed"); });
      });
      life.on(document, "gdk:motion-change", function () { if (!Motion.enabled) reveal(true); });

      if (!Motion.enabled || !("IntersectionObserver" in window)) {
        reveal(true);
        return;
      }

      section.setAttribute("data-gdk-reveal-ready", "true");
      items.forEach(function (item) { item.classList.add("is-gdk-reveal-pending"); });
      if (section.getBoundingClientRect().top < window.innerHeight * .88) {
        reveal(true);
        return;
      }
      observer = new IntersectionObserver(function (entries) {
        if (!life.active || !entries.some(function (entry) { return entry.isIntersecting; })) return;
        reveal(false);
      }, { threshold: .12, rootMargin: "0px 0px -8%" });
      observer.observe(section);
    });
  }

  /* Compact mobile footer accordions; desktop remains fully expanded. */
  function initFooterAccordions(root) {
    componentElements("[data-gdk-footer-accordion]", root || document).forEach(function (accordion) {
      var life = componentScope(accordion, "footer");
      if (!life) return;
      var trigger = one("[data-footer-toggle]", accordion);
      if (!trigger) return;
      var mobileQuery = window.matchMedia("(max-width: 767px)");
      var syncState = function () {
        trigger.setAttribute("aria-expanded", mobileQuery.matches && !accordion.classList.contains("is-open") ? "false" : "true");
      };
      life.on(trigger, "click", function () {
        var open = accordion.classList.toggle("is-open");
        trigger.setAttribute("aria-expanded", open ? "true" : "false");
      });
      life.media(mobileQuery, syncState);
      syncState();
    });
  }

  /* Predictive search: collections/clubs first, then products and content. */
  var predictiveOptionSerial = 0;

  function isConfiguredServiceProduct(item) {
    if (!item) return false;
    var serviceIds = [config.serviceProductId].concat(config.serviceProductIds || []).map(Number);
    if (item.product_id && serviceIds.indexOf(Number(item.product_id)) !== -1) return true;
    if (item.id && serviceIds.indexOf(Number(item.id)) !== -1) return true;
    if (!item.url) return false;
    var serviceUrls = [config.serviceProductUrl].concat(config.serviceProductUrls || []).filter(Boolean);
    return serviceUrls.some(function (serviceUrl) {
      try {
        return new URL(item.url, window.location.origin).pathname === new URL(serviceUrl, window.location.origin).pathname;
      } catch (error) {
        return item.url === serviceUrl;
      }
    });
  }

  function preparePredictiveMarkup(target) {
    all("[data-gdk-predictive-group]", target).forEach(function (group) {
      var heading = one("[data-gdk-predictive-group-heading]", group);
      if (!heading) return;
      heading.id = "GdkPredictiveGroup-" + (++predictiveOptionSerial);
      group.setAttribute("role", "group");
      group.setAttribute("aria-labelledby", heading.id);
    });
    all("[role='option']", target).forEach(function (option) {
      option.id = "GdkPredictiveOption-" + (++predictiveOptionSerial);
      option.setAttribute("aria-selected", "false");
      option.tabIndex = -1;
    });
  }

  function initPredictiveSearch(root) {
    componentElements("[data-predictive-input]", root).forEach(function (input) {
      var life = componentScope(input, "predictive");
      if (!life) return;
      var predictiveScope = input.closest("[data-predictive-search-scope]") || input.closest("[data-overlay='search']") || document;
      var results = one("[data-predictive-results]", predictiveScope);
      if (!results) return;
      var popular = one("[data-search-popular]", results);
      var serverResults = one("[data-search-server-results]", predictiveScope);
      var initialQuery = String(input.getAttribute("data-initial-query") || "").trim();
      var timer;
      var controller;
      life.add(function () {
        if (controller) controller.abort();
        controller = null;
        results.removeAttribute("aria-busy");
        input.removeAttribute("aria-activedescendant");
        input.setAttribute("aria-expanded", "false");
      });

      function resetActiveOption() {
        input.removeAttribute("aria-activedescendant");
        all("[role='option']", results).forEach(function (option) {
          option.classList.remove("is-active");
          option.setAttribute("aria-selected", "false");
        });
      }

      function setResultsMode(isListbox) {
        results.setAttribute("role", isListbox ? "listbox" : "region");
        resetActiveOption();
      }

      life.on(input, "keydown", function (event) {
        if (event.key === "Escape") {
          life.clearTimeout(timer);
          if (controller) controller.abort();
          controller = null;
          results.replaceChildren();
          if (popular) results.appendChild(popular);
          results.removeAttribute("aria-busy");
          setResultsMode(false);
          input.setAttribute("aria-expanded", "false");
          return;
        }
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return;
        var options = all("[role='option']", results);
        if (!options.length) return;
        var activeId = input.getAttribute("aria-activedescendant");
        var activeIndex = options.findIndex(function (option) { return option.id === activeId; });
        if (event.key === "Enter") {
          if (activeIndex < 0) return;
          event.preventDefault();
          window.location.assign(options[activeIndex].href);
          return;
        }
        event.preventDefault();
        activeIndex = event.key === "ArrowDown"
          ? (activeIndex + 1) % options.length
          : (activeIndex <= 0 ? options.length - 1 : activeIndex - 1);
        options.forEach(function (option, index) {
          var active = index === activeIndex;
          option.classList.toggle("is-active", active);
          option.setAttribute("aria-selected", active ? "true" : "false");
        });
        input.setAttribute("aria-activedescendant", options[activeIndex].id);
        options[activeIndex].scrollIntoView({ block: "nearest" });
      });

      life.on(input, "input", function () {
        life.clearTimeout(timer);
        if (controller) controller.abort();
        controller = null;
        resetActiveOption();
        var query = input.value.trim();
        if (serverResults) serverResults.hidden = Boolean(query && query !== initialQuery);
        if (query.length < 2) {
          results.replaceChildren();
          if (popular) results.appendChild(popular);
          results.removeAttribute("aria-busy");
          setResultsMode(false);
          input.setAttribute("aria-expanded", "false");
          return;
        }
        if (query.length < 4) {
          results.replaceChildren(create("p", "gdk-predictive__hint", "Keep typing to find your side…"));
          results.removeAttribute("aria-busy");
          setResultsMode(false);
          input.setAttribute("aria-expanded", "false");
          return;
        }
        results.replaceChildren(create("p", "gdk-predictive__hint", "Searching…"));
        results.setAttribute("aria-busy", "true");
        setResultsMode(false);
        input.setAttribute("aria-expanded", "false");
        timer = life.timeout(function () {
          var requestQuery = query;
          var requestController = new AbortController();
          controller = requestController;
          var endpoint = routes.predictiveSearch || (routes.search || "/search") + "/suggest";
          endpoint = endpoint.replace(/\.json(?=\?|$)/, "");
          var searchUrl = endpoint + "?q=" + encodeURIComponent(query) + "&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last&section_id=gdk-predictive-search";
          fetch(searchUrl, { signal: requestController.signal, headers: { Accept: "text/html" } })
            .then(function (response) {
              return response.text().then(function (html) {
                if (!response.ok) throw new Error("Predictive search failed");
                return html;
              });
            })
            .then(function (html) {
              if (!life.active || requestController.signal.aborted || controller !== requestController || input.value.trim() !== requestQuery) return;
              var parsed = new DOMParser().parseFromString(html, "text/html");
              var content = one("#shopify-section-gdk-predictive-search [data-gdk-predictive-content]", parsed);
              if (!content) throw new Error("Predictive search markup is unavailable");
              var nodes = Array.from(content.childNodes).map(function (node) { return node.cloneNode(true); });
              results.replaceChildren.apply(results, nodes);
              preparePredictiveMarkup(results);
              var hasOptions = Boolean(one("[role='option']", results));
              setResultsMode(hasOptions);
              results.removeAttribute("aria-busy");
              input.setAttribute("aria-expanded", hasOptions ? "true" : "false");
              track("predictive_search", { query: requestQuery });
            })
            .catch(function (error) {
              if (error.name === "AbortError") return;
              if (!life.active || requestController.signal.aborted || controller !== requestController || input.value.trim() !== requestQuery) return;
              results.replaceChildren(create("p", "gdk-form-error", "Search is temporarily unavailable. Use the search button to continue."));
              results.removeAttribute("aria-busy");
              setResultsMode(false);
              input.setAttribute("aria-expanded", "false");
            });
        }, 240);
      });
      life.on(document, "gdk:overlay:close", function (event) {
        if (!event.detail || !event.detail.overlay || !event.detail.overlay.contains(input)) return;
        life.clearTimeout(timer);
        if (controller) controller.abort();
        controller = null;
        input.value = "";
        results.replaceChildren();
        if (popular) results.appendChild(popular);
        results.removeAttribute("aria-busy");
        setResultsMode(false);
        input.setAttribute("aria-expanded", "false");
      });
    });
  }

  function initRecommendations(root) {
    componentElements("product-recommendations", root).forEach(function (element) {
      var life = componentScope(element, "recommendations");
      if (!life) return;
      var controller;
      life.add(function () { if (controller) controller.abort(); });
      var load = function () {
        if (!element.dataset.url || !life.active) return;
        controller = new AbortController();
        fetch(element.dataset.url, { signal: controller.signal })
          .then(function (response) { return response.text(); })
          .then(function (html) {
            if (!life.active || !element.isConnected) return;
            var documentFragment = new DOMParser().parseFromString(html, "text/html");
            var replacement = documentFragment.querySelector("product-recommendations");
            if (!replacement) return;
            Array.from(element.children).forEach(disposeComponents);
            element.replaceChildren.apply(element, Array.from(replacement.childNodes).map(function (node) { return node.cloneNode(true); }));
            init(element);
            applyMyClub(readMyClub(), { skipWorldStage: true });
            prunePdpRecommendationDupes(element);
          })
          .catch(function () {});
      };
      if ("IntersectionObserver" in window) {
        var observer = new IntersectionObserver(function (entries) {
          if (!life.active || !entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        }, { rootMargin: "0px 0px 400px" });
        life.add(function () { observer.disconnect(); });
        observer.observe(element);
      } else load();
    });
  }

  function initReviewNavigation(root) {
    componentElements("[data-review-jump]", root).forEach(function (link) {
      var life = componentScope(link, "review-navigation");
      if (!life) return;
      life.on(link, "click", function (event) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        var target = document.getElementById("reviews");
        if (!target || !target.hasAttribute("data-matchday-reviews")) return;
        event.preventDefault();
        /* A direct arrival is deliberately brief, including with reduced motion. */
        target.scrollIntoView({ behavior: "instant", block: "start" });
        target.focus({ preventScroll: true });
        window.history.replaceState(null, "", "#reviews");
      });
    });
  }

  function initForms(root) {
    componentElements("[data-sort-select]", root).forEach(function (select) {
      var life = componentScope(select, "initForms");
      if (!life) return;
      life.on(select, "change", function () {
        var form = select.closest("form");
        if (form) form.requestSubmit();
      });
    });
    componentElements("form[role='search']", root).forEach(function (form) {
      var life = componentScope(form, "initForms");
      if (!life) return;
      life.on(form, "submit", function () {
        var input = one("input[name='q']", form);
        track("search", { query: input ? input.value.trim() : "" });
      });
    });
  }

  function initFloatingSupport(root) {
    var link = one(".gdk-whatsapp-link--floating", root || document) || one(".gdk-whatsapp-link--floating");
    if (!link) return;
    var life = componentScope(link, "floating-support");
    if (!life) return;
    if (!("IntersectionObserver" in window)) return;
    var releaseVisibility = function () {};
    link._gdkRefreshVisibility = function (excludedRoot) {
      releaseVisibility();
      var zones = all(".gdk-home-end, .gdk-international, .gdk-personalisation-feature, .gdk-final-cta, .gdk-footer").filter(function (zone) {
        return !(excludedRoot && (zone === excludedRoot || excludedRoot.contains(zone)));
      });
      var watching = true;
      var visible = new Set();
      var observer = new IntersectionObserver(function (entries) {
        if (!life.active || !watching) return;
        entries.forEach(function (entry) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        link.classList.toggle("is-suppressed", visible.size > 0);
      }, { threshold: 0.08 });
      releaseVisibility = life.add(function () { watching = false; observer.disconnect(); visible.clear(); });
      zones.forEach(function (zone) { observer.observe(zone); });
      if (!zones.length) link.classList.remove("is-suppressed");
    };
    life.add(function () {
      delete link._gdkRefreshVisibility;
      link.classList.remove("is-suppressed");
    });
    link._gdkRefreshVisibility();
  }

  function refreshVisibilityTargets(excludedRoot) {
    all("[data-product-root], .gdk-whatsapp-link--floating").forEach(function (element) {
      if (excludedRoot && (element === excludedRoot || excludedRoot.contains(element))) return;
      if (element._gdkRefreshVisibility) element._gdkRefreshVisibility(excludedRoot);
    });
  }

  function initKeyboardContext() {
    if (document.body.dataset.gdkKeyboardContextReady === "true") return;
    document.body.dataset.gdkKeyboardContextReady = "true";
    var isTextEntry = function (element) {
      return Boolean(element && element.matches && element.matches("textarea, select, [contenteditable='true'], input:not([type='button']):not([type='submit']):not([type='reset']):not([type='checkbox']):not([type='radio']):not([type='range']):not([type='color']):not([type='file']):not([type='hidden'])"));
    };
    var setState = function (active) {
      var changed = document.body.classList.contains("gdk-keyboard-context") !== active;
      document.body.classList.toggle("gdk-keyboard-context", active);
      if (changed) document.dispatchEvent(new CustomEvent("gdk:keyboard-context", { detail: { active: active } }));
    };
    document.addEventListener("shopify:section:unload", function (event) {
      if (event.target.contains(document.activeElement)) setState(false);
    });
    document.addEventListener("focusin", function (event) { setState(isTextEntry(event.target)); });
    document.addEventListener("focusout", function () {
      window.setTimeout(function () { setState(isTextEntry(document.activeElement)); }, 0);
    });
  }

  function initTracking(root) {
    componentElements("[data-whatsapp-link]", root).forEach(function (link) {
      var life = componentScope(link, "initTracking");
      if (!life) return;
      life.on(link, "click", function () { track("whatsapp", { location: document.body.dataset.template || "unknown" }); });
    });
    componentElements("button[name='checkout']", root).forEach(function (button) {
      var life = componentScope(button, "initTracking");
      if (!life) return;
      life.on(button, "click", function () { track("begin_checkout", {}); });
    });
  }

  // Fit only overflowing verified names; preserve the CSS name slot and number position.
  function initPersonalisationNameFit(root) {
    componentElements("[data-preview-name], [data-home-preview-name]", root).forEach(function (name) {
      var life = componentScope(name, "name-fit");
      if (!life) return;
      var host = name.closest("[data-gdk-pers-applied]"), frame = 0;
      function fit() {
        frame = 0;
        name.style.removeProperty("font-size");
        if (!host || host.getAttribute("data-gdk-pers-applied") !== "verified" || name.hidden) return;
        var available = name.parentElement.getBoundingClientRect().width;
        if (!available) return;
        var range = document.createRange();
        range.selectNodeContents(name);
        var width = range.getBoundingClientRect().width;
        if (width > available - 2) name.style.fontSize = (parseFloat(getComputedStyle(name).fontSize) * (available - 2) / width) + "px";
      }
      function schedule() { if (!frame && life.active) frame = life.frame(fit); }
      var text = new MutationObserver(schedule);
      text.observe(name, { childList: true, characterData: true, subtree: true });
      life.add(function () { text.disconnect(); name.style.removeProperty("font-size"); });
      if (host) {
        var style = new MutationObserver(schedule);
        style.observe(host, { attributes: true, attributeFilter: ["data-gdk-pers-applied"] });
        life.add(function () { style.disconnect(); });
      }
      if (window.ResizeObserver) {
        var resize = new ResizeObserver(schedule);
        resize.observe(name.parentElement);
        life.add(function () { resize.disconnect(); });
      } else life.on(window, "resize", schedule);
      if (document.fonts) document.fonts.ready.then(schedule);
      schedule();
    });
  }

  function init(root) {
    var scope = root || document;
    initMegaMenus(scope);
    initProduct(scope);
    initGalleries(scope);
    initTabs(scope);
    initWorldProductStages(scope);
    initClubGroups(scope);
    initHomepagePersonalisation(scope);
    initPersonalisationNameFit(scope);
    initHomepageFlow(scope);
    initFooterAccordions(scope);
    initMyClub(scope);
    initPredictiveSearch(scope);
    initRecommendations(scope);
    initReviewNavigation(scope);
    initForms(scope);
    initTracking(scope);
    initFloatingSupport(scope);
    initKeyboardContext();
  }

  document.addEventListener("DOMContentLoaded", function () {
    init(document);
    if (one("[data-cart-page]")) refreshCart().catch(function () {});
  });

  document.addEventListener("shopify:section:load", function (event) {
    Motion.sync();
    init(event.target);
    if (currentCart && (event.target.matches("[data-cart-page]") || one("[data-cart-body], [data-cart-page-lines]", event.target))) renderCart(currentCart);
    applyCartState();
    refreshVisibilityTargets();
    OverlayController.reconcile();
  });
  document.addEventListener("shopify:section:unload", function (event) {
    OverlayController.dispose(event.target);
    if (quickAddOrigin && event.target.contains(quickAddOrigin)) clearQuickAddPresentation();
    disposeComponents(event.target);
    refreshVisibilityTargets(event.target);
  });
  document.addEventListener("shopify:section:reorder", function () {
    refreshVisibilityTargets();
    OverlayController.reconcile();
  });
  document.addEventListener("shopify:block:select", function (event) {
    var tabs = event.target.closest("[data-gdk-tabs]");
    if (tabs && tabs._gdkSelectBlock) tabs._gdkSelectBlock(event.target);
    var clubSection = event.target.closest("[data-gdk-club-groups]");
    var clubItem = event.target.closest("[data-club-item]");
    if (clubSection && clubItem) activateClubGroup(clubSection, clubItem.getAttribute("data-club-group"));
  });

  window.GDKThemeAPI = {
    foundationVersion: "31.0",
    commerceVersion: "31.1",
    experienceVersion: "31.3",
    motion: Motion,
    overlays: OverlayController,
    refreshCart: refreshCart,
    addItems: addItems,
    formatMoney: formatMoney,
    track: track
  };
})();
